import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import config from '../config.js';
import pinPrompt from '../utils/pinPrompt';
import { CoatingColorField } from '../components/CoatingColor';
import AssembliesManagerPricingCard from '../components/AssembliesManagerPricingCard';
import { setAiContext, clearAiContext } from '../utils/aiContext';
import { apiErrorMessage, detailToString } from '../utils/apiError';
import { fmtMoney, fmtDelta, fmtRate, fmtMinutes, fmtPct, asOf, sourceBadgeClass } from '../utils/priceMath';
import {
  ASSEMBLY_DEFAULT_QUOTE_RATES, ASSEMBLY_OP_COST_RATES, DEFAULT_RATE_SET_NAME, pricingQueryString, rateSetLabel,
  componentCostView, componentRollupFormula, consumableView, estimateOpsFromGrid, opsRowsView,
  buildLocalCardModel, normalizePricingResponse, enrichServerLinesFromComponents, applyOpsEstimate, applyPpq,
  perSetTotals, perSetDrift,
  assemblyPriceSaveDecision, rollupPatchPayload, priceProvenanceLabel,
} from '../utils/assembliesManagerPricing';
import {
  BLANK_HEADER_FIELDS, normalizeHeader, changedHeaderLabels, processRowsSignature, gridRowsFromApi,
  opsRowsPayload, validateOpsRows, planAssemblySave, noOpsWarning, OPS_CLEARED_MESSAGE,
  unsavedItems, unsavedChangesMessage, isLeavingPageClick,
} from '../utils/assemblyFormState';

/**
 * AssembliesManagerPage
 * * Enhanced to display a detailed cost and price breakdown for assembly components.
 * * ADDED: Production Recipe Management (Cut Sheets/Lists definitions).
 * * ADDED: "Build in Sets" and "Primary Part Quantity" logic.
 * * ADDED: Consumable-based Components support.
 * * UPDATED: Added Assembly Customer (Owner) field.
 * * FIXED: Total Price calculation now includes Consumables.
 * * UPDATED: Added Sidebar Sorting and Filtering (Customer, Search, Date/Name).
 * * UPDATED: Added Book Price and Price Subtotal to Purchased Components table.
 */
// Cost/price arithmetic for this page lives in src/utils/assembliesManagerPricing.js
// (ASSEMBLY_OP_COST_RATES — the unsaved-grid wage-rate table — included) so it
// is unit-tested and shared with the pricing card component.

export default function AssembliesManagerPage() {
  const API_BASE = config.API_BASE_URL;

  // Define Fabrication sub-processes for logic reuse
  const FABRICATION_SUB_PROCESSES = useMemo(() => [
    'Welding', 'Cutting', 'Drilling', 'Assembly', 'Tapping', 'Milling', 'Lathe'
  ], []);

  // ================================
  // Assemblies Data
  // ================================
  const [assemblies, setAssemblies] = useState([]);
  const [selectedAssembly, setSelectedAssembly] = useState(null);

  // Form fields for the current assembly
  const [assemblyId, setAssemblyId] = useState(null);
  const [assemblyName, setAssemblyName] = useState("");
  const [assemblyCustomerId, setAssemblyCustomerId] = useState(""); // NEW: Customer ID State
  const [assemblyNotes, setAssemblyNotes] = useState("");
  const [assemblyPrice, setAssemblyPrice] = useState("");

  const [assemblyPrimaryPartProdId, setAssemblyPrimaryPartProdId] = useState("");
  const [primaryPartQuantity, setPrimaryPartQuantity] = useState(1);
  const [joinWorkstation, setJoinWorkstation] = useState("");

  // Assembly Operations grid (assembly_processes — design §8 Phase 3).
  // Rows are per-ASSEMBLY: {step, minutes, coatCost, coatVendor, coatColor*, notes}.
  // The coatColor* triple (code/name/hex) is the requested finish color and
  // applies to Coating rows only; it overrides the primary part's color.
  const [processRows, setProcessRows] = useState([]);
  // Authoritative ops cost from POST /quotes/calculate-assembly-ops — the SAME
  // endpoint the quote line prices from, so this card and the quote can never
  // disagree. It reads SAVED assembly_processes rows, so it is only shown when
  // the grid matches what's saved; while there are unsaved edits we fall back
  // to the local standard-rate estimate and say so.
  const [opsAuth, setOpsAuth] = useState(null);          // {total_cost, operations} | null
  // GET /assemblies/{id}/pricing — the ONE per-set cost/price contract this
  // page shares with the quote page (spec §3 B4). Not deployed everywhere yet:
  // 'unavailable' means the API 404'd the route and the card falls back to
  // stored rollups + the saved ops calculation, and says so.
  // {status:'idle'|'loading'|'ok'|'unavailable'|'error', data, error, fetchedAt}
  const [pricingInfo, setPricingInfo] = useState({ status: 'idle', data: null, error: null, fetchedAt: null });
  const pricingReqRef = useRef(null); // latest assembly asked for, so a slow older reply is ignored
  // Primary part summary from the assembly response (`primary_part`, B3) or a
  // fresh GET /parts/{id} on select — never the mount-time parts list (F21).
  const [primaryPart, setPrimaryPart] = useState(null);
  // What assembly_price the form was seeded with, so Save sends it only when
  // it changed and confirms before clearing a real value (F51).
  const [loadedAssemblyPrice, setLoadedAssemblyPrice] = useState(null);
  const [recalcBusy, setRecalcBusy] = useState(false);
  const [savedProcessSig, setSavedProcessSig] = useState("");
  const [processesMsg, setProcessesMsg] = useState(null);
  // What the server holds, for the unsaved-changes warnings: the header as
  // normalizeHeader() sees it, and every field of every operation row. Both
  // are set only from server data (or the blank new form), never from edits.
  const [savedHeader, setSavedHeader] = useState(() => normalizeHeader(BLANK_HEADER_FIELDS));
  const [savedOpsFullSig, setSavedOpsFullSig] = useState(() => processRowsSignature([]));
  const [savedOpsCount, setSavedOpsCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [vendorsList, setVendorsList] = useState([]);
  const [joinSubProcess, setJoinSubProcess] = useState("");
  const [assemblyComponents, setAssemblyComponents] = useState([]);
  // Resolved part-steps + operations chain — what jobs from this assembly will
  // ACTUALLY run. Read-only; the API computes it with the same merge the floor
  // uses, so this can't drift from reality.
  const [effChain, setEffChain] = useState(null);

  // Sidebar: hide retired assemblies by default. The list loads with
  // ?include_inactive=true because this is the only screen that can restore
  // one, so the filtering has to happen HERE — without it every assembly the
  // nightly sweep has ever retired sits in the list permanently, with no way
  // to get it out of the way.
  const [showInactive, setShowInactive] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // Superseded part-revision awareness (GET /assemblies/{id}/revision_status)
  const [revStatus, setRevStatus] = useState(null);
  const [revBusy, setRevBusy] = useState(false);
  
  const [buildSetQuantity, setBuildSetQuantity] = useState("");

  // Shipping profile — final assembled-product dims/weight/stacking. Drives
  // the packaging planner's assembly-unit packing (jobq-api packaging_guide).
  const [shipLengthIn, setShipLengthIn] = useState("");
  const [shipWidthIn, setShipWidthIn] = useState("");
  const [shipHeightIn, setShipHeightIn] = useState("");
  const [shipWeightLbs, setShipWeightLbs] = useState("");
  const [shipStackable, setShipStackable] = useState(true);
  const [shipMaxStackLayers, setShipMaxStackLayers] = useState("");

  // ================================
  // NEW: State for Consumable-based Components
  // ================================
  const [consumablesList, setConsumablesList] = useState([]);
  const [assemblyConsumables, setAssemblyConsumables] = useState([]);
  const [newConsumableId, setNewConsumableId] = useState("");
  const [newConsumableQty, setNewConsumableQty] = useState("1");
  const [editingConsumableId, setEditingConsumableId] = useState(null);
  const [editingConsumableQty, setEditingConsumableQty] = useState("");
  // ships_loose: bolt-on items that ship loose with the assembly (kit box / bundle on the packing plan)
  const [editingConsumableLoose, setEditingConsumableLoose] = useState(false);


  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assemblyWeight, setAssemblyWeight] = useState(null);

  // ================================
  // Parts & Production Data
  // ================================
  const [partsList, setPartsList] = useState([]);
  const [customersList, setCustomersList] = useState([]); 
  
  // NEW: Data needed for Recipes
  const [cutSheetList, setCutSheetList] = useState([]);
  const [cutListList, setCutListList] = useState([]);

  // Filters
  const [primaryPartCustomerFilter, setPrimaryPartCustomerFilter] = useState(""); 
  const [componentPartCustomerFilter, setComponentPartCustomerFilter] = useState(""); 

  // ================================
  // Sidebar Filters & Sorting
  // ================================
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCustomerId, setFilterCustomerId] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");

  // For adding a new component
  const [newCompProductId, setNewCompProductId] = useState("");
  const [newCompQty, setNewCompQty] = useState("1");

  // Inline editing of a component
  const [editingCompId, setEditingCompId] = useState(null);
  const [editingCompQty, setEditingCompQty] = useState("");

  // Enlarged image overlay
  const [enlargedImage, setEnlargedImage] = useState(null);

  // Assembly image upload
  const [assemblyImageFile, setAssemblyImageFile] = useState(null);

  // ================================
  // NEW: Production Recipe State
  // ================================
  const [recipes, setRecipes] = useState([]);
  const [yieldInfo, setYieldInfo] = useState(null);
  
  // State for creating/editing a recipe in UI memory before saving
  const [recipeName, setRecipeName] = useState("Standard Build");
  const [isDefaultRecipe, setIsDefaultRecipe] = useState(true);
  const [pendingRecipeItems, setPendingRecipeItems] = useState([]); // Array of { type: 'Cut Sheet'|'Cut List', id, name, qty }
  
  // Inputs for adding a row to the recipe
  const [newRecipeItemType, setNewRecipeItemType] = useState("Cut Sheet");
  const [newRecipeItemId, setNewRecipeItemId] = useState(""); // ID of selected sheet/list
  const [newRecipeItemQty, setNewRecipeItemQty] = useState("1");


  // ================================
  // Cost & price model (per set) — arithmetic in src/utils/assembliesManagerPricing.js
  // ================================
  // Components with the EFFECTIVE primary row: qty from the Primary Part Qty
  // input (so the tables and card move as you type, labelled unsaved until
  // saved — F21) and cost/price from `primaryPart` (fetched fresh on select),
  // never the mount-time parts list.
  const effectiveComponents = useMemo(() => assemblyComponents.map((c) => {
    if (!c.is_primary) return c;
    const fresh = primaryPart && primaryPart.part_id === c.part_id ? primaryPart : {};
    return {
      ...c, ...fresh,
      is_primary: true,
      assembly_component_id: c.assembly_component_id,
      quantity_per_assembly: Math.max(1, parseInt(primaryPartQuantity, 10) || 1),
    };
  }), [assemblyComponents, primaryPart, primaryPartQuantity]);
  const ppqUnsaved = !!selectedAssembly
    && (Math.max(1, parseInt(primaryPartQuantity, 10) || 1) !== (selectedAssembly.primary_part_quantity || 1));
  const staleComponentCount = useMemo(
    () => effectiveComponents.filter((c) => componentCostView(c).stale).length,
    [effectiveComponents]
  );

  // Assembly OPERATIONS (at/after the join) — the welding that joins the
  // parts, the outsourced coating, the finish. FALLBACK ONLY, used while the
  // grid has unsaved edits: it costs labour at the standard WAGE table,
  // whereas the saved figure (calculate-assembly-ops) uses live 90-day
  // punch-weighted wages, so the two differ by a little (assembly #67:
  // $238.67 here vs the quote's $238.23). Coating is a typed dollar and
  // carries across exactly. The ops PRICE is unknowable until the rows are
  // saved, so while dirty the card's quotable price reads n/a.
  const localOps = useMemo(() => estimateOpsFromGrid(processRows, ASSEMBLY_OP_COST_RATES), [processRows]);


  // State for Assembly Documents
  const [assemblyDocuments, setAssemblyDocuments] = useState([]);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [isUploading, setIsUploading] = useState(false);


  // Clear AI context on unmount
  useEffect(() => () => clearAiContext(), []);

  // ================================
  // useEffects => load on mount
  // ================================
  useEffect(() => {
    loadAssemblies();
    loadParts();
    loadCustomers();
    loadConsumableComponents();
    loadCutSheets();
    loadCutLists();
    // Vendors for the Assembly Operations grid's Coating rows
    fetch(`${API_BASE}/vendors`)
      .then(r => (r.ok ? r.json() : []))
      .then(v => setVendorsList(Array.isArray(v) ? v : []))
      .catch(() => setVendorsList([]));
  }, []);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") {
        closeEnlarge();
      }
    }
    if (enlargedImage) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enlargedImage]);

  // ================================
  // Filter Logic (Memoized)
  // ================================
  
  // Primary Part & Component Filters
  const filteredPrimaryParts = useMemo(() => {
    if (!primaryPartCustomerFilter) return partsList;
    const custId = parseInt(primaryPartCustomerFilter, 10);
    return partsList.filter(p => p.customer_id === custId);
  }, [partsList, primaryPartCustomerFilter]);

  const filteredComponentParts = useMemo(() => {
    // Never offer the current primary as a component (it's implicit via Primary Part Qty)
    let list = assemblyPrimaryPartProdId
      ? partsList.filter(p => p.product_id !== assemblyPrimaryPartProdId)
      : partsList;
    if (!componentPartCustomerFilter) return list;
    const custId = parseInt(componentPartCustomerFilter, 10);
    return list.filter(p => p.customer_id === custId);
  }, [partsList, componentPartCustomerFilter, assemblyPrimaryPartProdId]);

  // Sidebar List Filters (Assemblies)
  const filteredAndSortedAssemblies = useMemo(() => {
      let result = assemblies.filter(a => {
          const term = searchTerm.toLowerCase();
          
          // Match Name or ID
          const matchesTerm = (a.assembly_name || "").toLowerCase().includes(term) || 
                              String(a.assembly_id).includes(term);
          
          // Match Customer
          const matchesCustomer = !filterCustomerId || a.customer_id === parseInt(filterCustomerId, 10);

          // Retired assemblies are hidden unless asked for. Compared against
          // false explicitly: active_status is undefined on older rows and on
          // a freshly created assembly, and those are active.
          const matchesActive = showInactive || a.active_status !== false;
          
          return matchesTerm && matchesCustomer && matchesActive;
      });

      // Sorting
      result.sort((a, b) => {
          switch(sortBy) {
              case "name_asc":
                  return (a.assembly_name || "").localeCompare(b.assembly_name || "");
              case "name_desc":
                  return (b.assembly_name || "").localeCompare(a.assembly_name || "");
              case "date_newest":
                  return (b.assembly_id || 0) - (a.assembly_id || 0);
              case "date_oldest":
                  return (a.assembly_id || 0) - (b.assembly_id || 0);
              default:
                  return 0;
          }
      });

      return result;
  }, [assemblies, searchTerm, filterCustomerId, sortBy, showInactive]);


  // How many retired assemblies the checkbox is currently hiding. Counted off
  // the full list, not the filtered one, so it does not change as you search.
  const inactiveCount = useMemo(
    () => assemblies.filter((a) => a.active_status === false).length,
    [assemblies]
  );

  async function loadAssemblies() {
    setLoading(true);
    setError(null);
    try {
      // include_inactive: the stale-catalog sweep retires assemblies along with
      // their primary part. This is the ONLY screen that can restore them, so it
      // has to be able to see them — everywhere else keeps the active-only default.
      const res = await fetch(`${API_BASE}/assemblies?include_inactive=true`);
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, "Failed to load assemblies"));
      }
      const data = await res.json();
      setAssemblies(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Restore an assembly and every part it is built from, in one call. Reloads
  // the parts list afterwards because the Primary Part dropdown is built from
  // the ACTIVE parts — until that refetch it would still render blank.
  async function reactivateAssembly(targetId) {
    if (!targetId) return;
    try {
      const res = await fetch(`${API_BASE}/assemblies/${targetId}/reactivate`, { method: "POST" });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const data = await res.json();
      await loadParts();
      await loadAssemblies();
      await refreshAssembly(targetId).catch(() => {});
      alert(data.message || "Assembly reactivated.");
    } catch (err) {
      alert("Could not reactivate: " + err.message);
    }
  }

  // Retire an assembly from the pickers. Deliberately NOT a form field: the
  // save guard refuses every edit while the Primary Part dropdown is blank,
  // which is exactly the state a swept assembly is in, so a switch wired to
  // Save Changes would be unusable on the rows most likely to need it. This
  // sends the one field on its own.
  //
  // Parts are untouched by design — a part can belong to other assemblies and
  // sit on live jobs. Restoring is the asymmetric half (POST .../reactivate),
  // because the sweep retires an assembly and its primary part together.
  async function archiveAssembly(targetId) {
    if (!targetId) return;
    setArchiving(true);
    try {
      // Pre-flight: say what still points at this assembly. Reported, not
      // enforced — parking an assembly that a pending quote references is the
      // actual reason to press this button, and it is one click to undo.
      let usage = null;
      try {
        const u = await fetch(`${API_BASE}/assemblies/${targetId}/usage`);
        if (u.ok) usage = await u.json();
      } catch { /* the confirm is still worth showing without it */ }

      const lines = [];
      if (usage) {
        const say = (label, block) =>
          block.count > 0 &&
          lines.push(`  • ${block.count} ${label}${block.count === 1 ? "" : "s"} (#${block.ids.join(", #")}${block.count > block.ids.length ? ", …" : ""})`);
        say("open sales order", usage.open_sales_orders);
        say("open quote", usage.open_quotes);
        say("open job", usage.open_jobs);
      }

      const name = usage?.assembly_name || `Assembly #${targetId}`;
      const msg = [
        `${name} will be hidden from the job, quote and part pickers.`,
        "",
        lines.length
          ? "It is still referenced by:\n" + lines.join("\n")
          : usage
            ? "Nothing open references it."
            : "(Could not check open references — proceeding anyway.)",
        "",
        "Its parts are NOT changed, and Reactivate restores it at any time.",
        "",
        "Mark it inactive?",
      ].join("\n");
      if (!window.confirm(msg)) return;

      const res = await fetch(`${API_BASE}/assemblies/${targetId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active_status: false }),
      });
      if (!res.ok) {
        const raw = await res.text();
        let detail = raw;
        try { const j = JSON.parse(raw); if (j && j.detail) detail = detailToString(j.detail); } catch { /* keep raw */ }
        throw new Error(detail || `Mark inactive failed (${res.status})`);
      }
      // Reveal inactive rows, or the assembly just archived would vanish from
      // the sidebar with no indication of where it went.
      setShowInactive(true);
      await loadAssemblies();
      await refreshAssembly(targetId).catch(() => {});
      alert(`${name} is now inactive.`);
    } catch (err) {
      alert("Could not mark inactive: " + err.message);
    } finally {
      setArchiving(false);
    }
  }

  async function loadParts() {
    try {
      const res = await fetch(`${API_BASE}/parts`);
      if (res.ok) setPartsList(await res.json());
    } catch (err) { console.error(err); }
  }

  async function loadCustomers() {
    try {
      const res = await fetch(`${API_BASE}/customers`);
      if (res.ok) setCustomersList((await res.json()).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) { console.error(err); }
  }

  async function loadConsumableComponents() {
    try {
      const res = await fetch(`${API_BASE}/consumables?consumable_type=component`);
      if (res.ok) setConsumablesList(await res.json());
    } catch (err) { console.error(err); }
  }

  async function loadCutSheets() {
    try {
      const res = await fetch(`${API_BASE}/cut_sheets?active_only=true`);
      if (res.ok) setCutSheetList(await res.json());
    } catch (err) { console.error(err); }
  }

  async function loadCutLists() {
    try {
      const res = await fetch(`${API_BASE}/cut_lists?active_only=true`);
      if (res.ok) setCutListList(await res.json());
    } catch (err) { console.error(err); }
  }

  // ================================
  // Recipe Data Loading
  // ================================
  async function loadRecipes(asmId) {
    try {
        const res = await fetch(`${API_BASE}/assemblies/${asmId}/recipes`);
        if (res.ok) {
            const data = await res.json();
            setRecipes(data);
            
            // If there is a default recipe, populate the pending items UI with it for easy viewing/editing
            const def = data.find(r => r.is_default) || data[0];
            if (def) {
                setRecipeName(def.recipe_name);
                setIsDefaultRecipe(def.is_default);
                
                // Map API items to UI items
                const mappedItems = def.items.map(item => ({
                    type: item.cut_sheet_id ? 'Cut Sheet' : 'Cut List',
                    id: item.cut_sheet_id || item.cut_list_id,
                    name: item.cut_sheet_name || item.cut_list_name,
                    qty: item.quantity_per_batch
                }));
                setPendingRecipeItems(mappedItems);
            } else {
                // Reset if no recipes found
                setPendingRecipeItems([]);
                setRecipeName("Standard Build");
            }
        }
    } catch (err) { console.error(err); }
  }

  async function loadYieldInfo(asmId) {
      try {
          const res = await fetch(`${API_BASE}/assemblies/${asmId}/yield_info`);
          if (res.ok) setYieldInfo(await res.json());
      } catch (err) { console.error(err); }
  }


  // ================================
  // Assembly CRUD
  // ================================
  // The header form's raw values, in the shape normalizeHeader() reads.
  const headerFields = {
    assemblyName, assemblyCustomerId, assemblyNotes, assemblyPrice, assemblyPrimaryPartProdId,
    primaryPartQuantity, joinWorkstation, joinSubProcess, buildSetQuantity,
    shipLengthIn, shipWidthIn, shipHeightIn, shipWeightLbs, shipStackable, shipMaxStackLayers,
  };

  function applyHeaderFields(f) {
    setAssemblyName(f.assemblyName);
    setAssemblyCustomerId(f.assemblyCustomerId);
    setAssemblyNotes(f.assemblyNotes);
    setAssemblyPrice(f.assemblyPrice);
    setAssemblyPrimaryPartProdId(f.assemblyPrimaryPartProdId);
    setPrimaryPartQuantity(f.primaryPartQuantity);
    setJoinWorkstation(f.joinWorkstation);
    setJoinSubProcess(f.joinSubProcess);
    setBuildSetQuantity(f.buildSetQuantity);
    setShipLengthIn(f.shipLengthIn);
    setShipWidthIn(f.shipWidthIn);
    setShipHeightIn(f.shipHeightIn);
    setShipWeightLbs(f.shipWeightLbs);
    setShipStackable(f.shipStackable);
    setShipMaxStackLayers(f.shipMaxStackLayers);
  }

  function handleCreateNew() {
    setSelectedAssembly(null);
    setAssemblyId(null);
    applyHeaderFields(BLANK_HEADER_FIELDS);
    setSavedHeader(normalizeHeader(BLANK_HEADER_FIELDS));
    setSavedOpsFullSig(processRowsSignature([]));
    setSavedOpsCount(0);
    setEffChain(null);
    setAssemblyComponents([]);
    setAssemblyConsumables([]);
    setRecipes([]);
    setYieldInfo(null);
    setPendingRecipeItems([]);
    setError(null);
    setEditingCompId(null);
    setEditingConsumableId(null);
    setPrimaryPartCustomerFilter("");
    setComponentPartCustomerFilter("");
    setAssemblyImageFile(null);
    setAssemblyWeight(null);
    setProcessRows([]);
    setSavedProcessSig(processSignature([]));
    setOpsAuth(null);
    setProcessesMsg(null);
    pricingReqRef.current = null;
    setPricingInfo({ status: 'idle', data: null, error: null, fetchedAt: null });
    setPrimaryPart(null);
    setLoadedAssemblyPrice(null);
  }

  // ── Assembly Operations grid (assembly_processes — design §8 Phase 3) ──
  const ASSEMBLY_OP_STEPS = [...FABRICATION_SUB_PROCESSES, "Tap", "Brake", "Blast", "Coating", "Finish"];
  const effectiveJoinStep = joinWorkstation === "Fabrication" ? joinSubProcess : joinWorkstation;

  // Identity of the rows as the SERVER sees them — step + the two priced
  // fields. Notes/colour/vendor don't move the cost, so editing them must not
  // make the authoritative figure look stale.
  const processSignature = (rows) => JSON.stringify(
    (rows || []).map(r => [r.step || "", String(parseFloat(r.minutes) || 0), String(parseFloat(r.coatCost) || 0)]));

  const opsDirty = processSignature(processRows) !== savedProcessSig;
  // The ops figure actually shown. Once the rows are saved, it is the SAME
  // number POST /quotes/calculate-assembly-ops gives the quote line, so this
  // card and the quote can never quietly disagree. Only while the grid is
  // dirty (or the calculator failed while rows exist) do we show the
  // standard-rate estimate, clearly labelled.
  const opsIsEstimate = opsDirty || (!opsAuth && processRows.length > 0);

  // ── Unsaved changes ──
  // Header and operations are saved by ONE Save (handleSaveAll). Anything that
  // would drop an edit first warns: switching assembly, New, leaving the page
  // through the menu, closing / reloading the tab.
  const currentHeader = normalizeHeader(headerFields);
  const headerChangedLabels = changedHeaderLabels(savedHeader, currentHeader);
  const opsChanged = processRowsSignature(processRows) !== savedOpsFullSig;
  const unsavedList = unsavedItems(headerChangedLabels, opsChanged);
  const hasUnsaved = unsavedList.length > 0;

  function confirmDiscard(action) {
    return !hasUnsaved || window.confirm(unsavedChangesMessage(unsavedList, action));
  }

  // The edits to carry across a reload of the SAME assembly (adding a
  // component, a document, a consumable… all re-read the assembly). Before,
  // that reload reset the form and silently dropped unsaved operation rows.
  function editsToKeep(id) {
    if (!hasUnsaved || id !== assemblyId) return null;
    return {
      header: headerChangedLabels.length > 0 ? { ...headerFields } : null,
      ops: opsChanged ? processRows : null,
    };
  }

  const unsavedMessageRef = useRef("");
  unsavedMessageRef.current = hasUnsaved ? unsavedChangesMessage(unsavedList, "Leaving this page") : "";

  // Closing or reloading the tab: the browser shows its own "Leave site?" box.
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsaved]);

  // Menu links: the app uses <BrowserRouter>, which cannot block a route
  // change, so a capture-phase click listener on the document asks first and,
  // on Cancel, stops the click before React Router's <Link> sees it.
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const onClick = (e) => {
      const anchor = e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!isLeavingPageClick(e, anchor, window.location)) return;
      if (window.confirm(unsavedMessageRef.current)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [hasUnsaved]);

  // ONE card model for both data paths: the server's /pricing response when we
  // have it, else stored rollups + the saved ops calc. While the grid is dirty
  // the local estimate replaces the ops leg; while the ppq input differs from
  // the saved value the primary line follows the input.
  const cardModel = useMemo(() => {
    const opsSaved = opsAuth ? {
      operations: opsAuth.breakdown?.operations || [],
      cost: opsAuth.total_cost,
      price: opsAuth.calculated_price,
      isEstimate: false,
    } : { operations: [], cost: 0, price: processRows.length === 0 ? 0 : null, isEstimate: false };
    const asmMeta = { ...(selectedAssembly || {}), assembly_id: assemblyId };
    let model;
    if (pricingInfo.status === 'ok' && pricingInfo.data) {
      model = normalizePricingResponse(pricingInfo.data, asmMeta);
      // The server line shape has no book price / coating per unit; the
      // component rows on this page do — fill them so the Book price row and
      // the coating adjustment are reproducible from the tables on screen.
      model = enrichServerLinesFromComponents(model, effectiveComponents);
      if (ppqUnsaved) model = applyPpq(model, primaryPartQuantity);
    } else {
      model = buildLocalCardModel({
        components: effectiveComponents,
        consumables: assemblyConsumables,
        ppq: primaryPartQuantity,
        ops: opsSaved,
        assembly: asmMeta,
        materialMarkup: ASSEMBLY_DEFAULT_QUOTE_RATES.material_markup,
      });
    }
    if (opsIsEstimate) model = applyOpsEstimate(model, localOps);
    return model;
  }, [pricingInfo, opsAuth, opsIsEstimate, localOps, effectiveComponents, assemblyConsumables,
      primaryPartQuantity, ppqUnsaved, selectedAssembly, assemblyId, processRows.length]);
  const cardTotals = useMemo(() => perSetTotals(cardModel), [cardModel]);
  const cardDrift = useMemo(() => perSetDrift(cardModel, cardTotals), [cardModel, cardTotals]);
  const opsTableRows = useMemo(() => opsRowsView(cardModel.operations.rows), [cardModel]);

  const loadOpsCost = useCallback(async (asmId) => {
    if (!asmId) { setOpsAuth(null); return; }
    try {
      // Explicit rate set (F22): the request used to send {assembly_id} only,
      // so the calculated_price came back at whatever the server defaulted to
      // with nothing on screen naming it. These are the quote-page defaults
      // and the card prints them next to the price.
      const res = await fetch(`${API_BASE}/quotes/calculate-assembly-ops`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assembly_id: asmId, ...ASSEMBLY_DEFAULT_QUOTE_RATES }),
      });
      // 404 = the assembly has no process rows yet. Not an error.
      setOpsAuth(res.ok ? await res.json() : null);
    } catch (e) {
      console.warn("assembly-ops cost lookup failed", e);
      setOpsAuth(null);
    }
  }, [API_BASE]);

  // GET /assemblies/{id}/pricing at the default quote rates (spec §3 B4).
  // 404/405/501 → 'unavailable' (route not deployed yet) and the card uses the
  // stored-rollup fallback; anything else non-OK → 'error' with the message.
  const loadPricing = useCallback(async (asmId) => {
    if (!asmId) {
      pricingReqRef.current = null;
      setPricingInfo({ status: 'idle', data: null, error: null, fetchedAt: null });
      return;
    }
    pricingReqRef.current = asmId;
    setPricingInfo((p) => ({ ...p, status: 'loading' }));
    try {
      const res = await fetch(`${API_BASE}/assemblies/${asmId}/pricing?${pricingQueryString(ASSEMBLY_DEFAULT_QUOTE_RATES)}`);
      if (pricingReqRef.current !== asmId) return; // a newer selection superseded this reply
      if (res.status === 404 || res.status === 405 || res.status === 501) {
        setPricingInfo({ status: 'unavailable', data: null, error: null, fetchedAt: new Date().toISOString() });
        return;
      }
      if (!res.ok) throw new Error(await apiErrorMessage(res, `HTTP ${res.status}`));
      const data = await res.json();
      if (pricingReqRef.current !== asmId) return;
      if (!data || typeof data !== 'object' || (data.assembly_id != null && Number(data.assembly_id) !== Number(asmId))) {
        throw new Error('pricing response did not match the selected assembly');
      }
      setPricingInfo({ status: 'ok', data, error: null, fetchedAt: new Date().toISOString() });
    } catch (e) {
      if (pricingReqRef.current !== asmId) return;
      setPricingInfo({ status: 'error', data: null, error: e.message || String(e), fetchedAt: new Date().toISOString() });
    }
  }, [API_BASE]);

  function updateProcessRow(idx, field, val) {
    setProcessRows(rows => rows.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  }
  function moveProcessRow(idx, dir) {
    setProcessRows(rows => {
      const j = idx + dir;
      if (j < 0 || j >= rows.length) return rows;
      const next = [...rows];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }
  function removeProcessRow(idx) {
    setProcessRows(rows => rows.filter((_, i) => i !== idx));
  }
  function addProcessRow() {
    setProcessRows(rows => [...rows, { step: "", minutes: "", coatCost: "", coatVendor: "",
                                   coatColorCode: "", coatColorName: "", coatColorHex: "", notes: "" }]);
  }

  // Load `asm` into the form. `keep` (from editsToKeep) carries the operator's
  // unsaved header / operation edits across a reload of the same assembly; the
  // saved snapshots always come from the server, so those edits stay marked
  // unsaved.
  async function handleSelectAssembly(asm, keep = null) {
    setSelectedAssembly(asm);
    setAssemblyId(asm.assembly_id);
    setAiContext('assembly', { assemblyId: asm.assembly_id, assemblyName: asm.assembly_name });
    setLoadedAssemblyPrice(asm.assembly_price ?? null);
    setError(null);
    setEditingCompId(null);
    setEditingConsumableId(null); 
    setFileToUpload(null);
    setAssemblyImageFile(null);
    setAssemblyDocuments(asm.documents || []);
    setPrimaryPartCustomerFilter("");
    // Pre-filter the Add-Component parts list to this assembly's owner customer
    // so components default to the same customer (still overridable via the
    // "Filter Parts by Customer" dropdown).
    setComponentPartCustomerFilter(asm.customer_id ? String(asm.customer_id) : "");

    let primaryProdId = "";
    if (asm.primary_part_id) {
      const foundPart = partsList.find((p) => p.part_id === asm.primary_part_id);
      if (foundPart) {
        primaryProdId = foundPart.product_id;
        // Intentionally do NOT pin "Filter Primary by Customer" to the current
        // primary's customer here. Pinning hid every other customer's parts from
        // the Primary Part dropdown, so an operator couldn't SWAP the primary to
        // a part from a different customer (or an internal/no-customer part). The
        // filter stays on "All Customers" (cleared above) as an optional
        // narrower; the current primary still shows because
        // assemblyPrimaryPartProdId is set below.
      }
    }
    const loadedJoinStation = asm.join_workstation || "";
    const isFabJoin = FABRICATION_SUB_PROCESSES.includes(loadedJoinStation);
    const loadedHeader = {
      assemblyName: asm.assembly_name || "",
      assemblyCustomerId: asm.customer_id || "",
      assemblyNotes: asm.notes || "",
      assemblyPrice: asm.assembly_price || "",
      assemblyPrimaryPartProdId: primaryProdId,
      primaryPartQuantity: asm.primary_part_quantity || 1,
      joinWorkstation: isFabJoin ? "Fabrication" : loadedJoinStation,
      joinSubProcess: isFabJoin ? loadedJoinStation : "",
      buildSetQuantity: asm.build_set_quantity || "",
      shipLengthIn: asm.ship_length_in ?? "",
      shipWidthIn: asm.ship_width_in ?? "",
      shipHeightIn: asm.ship_height_in ?? "",
      shipWeightLbs: asm.ship_weight_lbs ?? "",
      shipStackable: asm.ship_stackable !== false,
      shipMaxStackLayers: asm.ship_max_stack_layers ?? "",
    };
    applyHeaderFields(keep?.header || loadedHeader);
    setSavedHeader(normalizeHeader(loadedHeader));

    // Assembly Operations rows (per-assembly at/after-join work)
    setProcessesMsg(null);
    // Per-set cost & price (independent of the fetches below; non-fatal).
    loadPricing(asm.assembly_id);
    try {
      const procRes = await fetch(`${API_BASE}/assemblies/${asm.assembly_id}/processes`);
      if (!procRes.ok) throw new Error(`HTTP ${procRes.status}`);
      const loadedRows = gridRowsFromApi(await procRes.json());
      setProcessRows(keep?.ops || loadedRows);
      setSavedProcessSig(processSignature(loadedRows));
      setSavedOpsFullSig(processRowsSignature(loadedRows));
      setSavedOpsCount(loadedRows.length);
      loadOpsCost(asm.assembly_id);
    } catch (e) {
      console.warn("Failed to load assembly processes", e);
      setProcessRows(keep?.ops || []);
      setSavedProcessSig(processSignature([]));
      setSavedOpsFullSig(processRowsSignature([]));
      setSavedOpsCount(0);
      setOpsAuth(null);
    }

    // Effective routing chain. Non-fatal: the page works without it.
    setEffChain(null);
    fetch(`${API_BASE}/assemblies/${asm.assembly_id}/effective_chain`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data && data.assembly_id === asm.assembly_id) setEffChain(data); })
      .catch(() => {});

    // Superseded part-revision status (warning banner + one-click cure).
    // Non-fatal: the page works without it.
    setRevStatus(null);
    fetch(`${API_BASE}/assemblies/${asm.assembly_id}/revision_status`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data && data.assembly_id === asm.assembly_id) setRevStatus(data); })
      .catch(() => {});

    try {
      // Fetch Components
      const compRes = await fetch(`${API_BASE}/assemblies/${asm.assembly_id}/components`);
      if (!compRes.ok) throw new Error("Failed to load assembly components");
      const subComponents = await compRes.json();
  
      // Fetch Consumables
      const consRes = await fetch(`${API_BASE}/assemblies/${asm.assembly_id}/consumable-components`);
      if (!consRes.ok) throw new Error("Failed to load assembly consumable components");
      const consumableComponents = await consRes.json();
      setAssemblyConsumables(consumableComponents);

      // Fetch calculated assembly weight
      fetch(`${API_BASE}/assemblies/${asm.assembly_id}/weight`)
        .then(res => res.ok ? res.json() : null)
        .then(data => setAssemblyWeight(data?.weight_lbs ?? null))
        .catch(() => setAssemblyWeight(null));
  
      let combinedComponents = [...subComponents];

      if (asm.primary_part_id) {
        // Primary part: a fresh GET /parts/{id} (full cost components, so the
        // row's live rollup can be checked) with the assembly response's
        // `primary_part` summary (spec B3: cost_updated_at, book-price
        // provenance) layered on top. The mount-time /parts list is only the
        // last resort — it is a snapshot from page load, so a part edited in
        // another tab never reached the primary row (F21).
        let primaryDetails = null;
        try {
          const pr = await fetch(`${API_BASE}/parts/${asm.primary_part_id}`);
          if (pr.ok) primaryDetails = await pr.json();
        } catch { /* fall through to the summary / list snapshot */ }
        const summary = (asm.primary_part && asm.primary_part.part_id === asm.primary_part_id) ? asm.primary_part : null;
        // Only the summary's REAL values win — a null there (no rollup yet, no
        // book price) must not blank a figure the full part row carries.
        const summaryValues = Object.fromEntries(
          Object.entries(summary || {}).filter(([, v]) => v !== null && v !== undefined)
        );
        const listed = partsList.find(p => p.part_id === asm.primary_part_id);
        const merged = { ...(listed || {}), ...(primaryDetails || {}), ...summaryValues };
        if (merged.part_id) {
          setPrimaryPart(merged);
          combinedComponents.unshift({
            ...merged,
            assembly_id: asm.assembly_id,
            assembly_component_id: `primary-${merged.part_id}`,
            quantity_per_assembly: asm.primary_part_quantity || 1,
            is_primary: true,
          });
        } else {
          setPrimaryPart(null);
        }
      } else {
        setPrimaryPart(null);
      }
      setAssemblyComponents(combinedComponents);
      
      // Load Recipes & Yield
      loadRecipes(asm.assembly_id);
      loadYieldInfo(asm.assembly_id);
  
    } catch (err) {
      setError(err.message);
      setAssemblyComponents([]);
      setAssemblyConsumables([]);
    }
  }

  // ONE save for everything on the form (header + Assembly Operations). Before
  // 2026-10-05 the operations had their own "Save Operations" button; rows
  // typed into the grid and then saved with "Save Changes" were never written
  // (assembly #128). Order matters on an existing assembly: the operations go
  // first because PUT /processes writes the join and its rows together, and
  // the PATCH guard refuses a join that matches none of the saved rows.
  async function handleSaveAll() {
    if (saving) return;
    setError(null);
    setProcessesMsg(null);
    const isNew = !assemblyId;
    const joinStep = effectiveJoinStep;

    if (!assemblyName.trim()) { alert("Assembly name is required."); return; }
    if (!assemblyPrimaryPartProdId) { alert("Please select a Primary Part for this assembly."); return; }
    if (!joinWorkstation) { alert("Please select the Join Station for this assembly."); return; }
    if (joinWorkstation === 'Fabrication' && !joinSubProcess) { alert("Please select a Join Sub-Process when using Fabrication."); return; }

    const found = partsList.find((p) => p.product_id === assemblyPrimaryPartProdId);
    if (!found) { alert("Please select a valid product from the dropdown for the primary part."); return; }
    const primaryPartIdToSave = found.part_id;

    const plan = planAssemblySave({
      isNew,
      opsChanged,
      joinChanged: currentHeader.joinStep !== savedHeader.joinStep,
      rowCount: processRows.length,
      savedRowCount: savedOpsCount,
    });
    if (plan.opsCleared) {
      setProcessesMsg({ type: "danger", text: OPS_CLEARED_MESSAGE });
      alert(OPS_CLEARED_MESSAGE);
      return;
    }
    if (plan.saveOps) {
      const opsError = validateOpsRows(processRows, joinStep);
      if (opsError) {
        setProcessesMsg({ type: "danger", text: opsError });
        alert(`Not saved. ${opsError}`);
        return;
      }
    }
    if (plan.noOps && !window.confirm(noOpsWarning(assemblyName.trim(), joinStep))) return;

    // F51: assembly_price rides along ONLY when the box changed, so an
    // unrelated header save can never rewrite (or null) the stored value;
    // clearing a real value asks first.
    const priceDecision = assemblyPriceSaveDecision(loadedAssemblyPrice, assemblyPrice);
    if (priceDecision.needsConfirm && !window.confirm(priceDecision.message)) return;

    const payload = {
      assembly_name: assemblyName,
      customer_id: assemblyCustomerId ? parseInt(assemblyCustomerId, 10) : null, // NEW
      notes: assemblyNotes,
      primary_part_id: primaryPartIdToSave,
      primary_part_quantity: primaryPartQuantity,
      join_workstation: joinStep,
      build_set_quantity: buildSetQuantity ? parseInt(buildSetQuantity, 10) : null,
      ship_length_in: shipLengthIn !== "" ? parseFloat(shipLengthIn) : null,
      ship_width_in: shipWidthIn !== "" ? parseFloat(shipWidthIn) : null,
      ship_height_in: shipHeightIn !== "" ? parseFloat(shipHeightIn) : null,
      ship_weight_lbs: shipWeightLbs !== "" ? parseFloat(shipWeightLbs) : null,
      ship_stackable: shipStackable,
      ship_max_stack_layers: shipMaxStackLayers !== "" ? parseInt(shipMaxStackLayers, 10) : null,
    };
    if (priceDecision.send) payload.assembly_price = priceDecision.value;

    const failText = async (res, fallback) => {
      const raw = await res.text();
      let detail = raw;
      try { const j = JSON.parse(raw); if (j && j.detail) detail = detailToString(j.detail); } catch { /* keep raw text */ }
      return detail || fallback;
    };
    const putOps = async (id) => {
      const res = await fetch(`${API_BASE}/assemblies/${id}/processes`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ join_workstation: joinStep, rows: opsRowsPayload(processRows, FABRICATION_SUB_PROCESSES) }),
      });
      if (!res.ok) throw new Error(await failText(res, `Save operations failed (${res.status})`));
    };
    const sendHeader = async () => {
      const res = await fetch(isNew ? `${API_BASE}/assemblies` : `${API_BASE}/assemblies/${assemblyId}`, {
        method: isNew ? "POST" : "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await failText(res, `Save assembly failed (${res.status})`));
      return res.json();
    };
    // After a write lands, the form's values ARE the saved values.
    const markHeaderSaved = () => setSavedHeader(normalizeHeader(headerFields));
    const markOpsSaved = () => {
      setSavedProcessSig(processSignature(processRows));
      setSavedOpsFullSig(processRowsSignature(processRows));
      setSavedOpsCount(processRows.length);
    };

    setSaving(true);
    let result = null;
    let opsSaved = false;
    try {
      if (!isNew && plan.saveOps) { await putOps(assemblyId); opsSaved = true; }
      result = await sendHeader();
      if (isNew && plan.saveOps) { await putOps(result.assembly_id); opsSaved = true; }
    } catch (err) {
      let msg = err.message;
      if (isNew && result) {
        // Created, but its operations were refused. Adopt the new id and keep
        // the grid exactly as typed (still unsaved) so it can be fixed and
        // saved again; a reload here would throw the rows away.
        msg = `Assembly #${result.assembly_id} was created, but its operations were NOT saved: ${err.message}`;
        setAssemblyId(result.assembly_id);
        setSelectedAssembly(result);
        setLoadedAssemblyPrice(result.assembly_price ?? null);
        markHeaderSaved();
        loadAssemblies();
      } else if (opsSaved) {
        msg = `The operations were saved, but the assembly details were NOT: ${err.message}`;
        markOpsSaved();
        loadOpsCost(assemblyId);
      } else if (plan.saveOps) {
        setProcessesMsg({ type: "danger", text: `Not saved: ${err.message}` });
      }
      setError(msg);
      alert(`Not saved. ${msg}`);
      return;
    } finally {
      setSaving(false);
    }

    const savedId = result.assembly_id;
    alert(isNew
      ? `Assembly created: #${savedId}${opsSaved ? " (with its operations)" : ""}`
      : `Assembly #${savedId} saved${opsSaved ? " (details and operations)" : ""}.`);
    loadAssemblies();
    // F21: reload from the server so the primary row's qty and cost, the
    // pricing card, the System Price provenance, the operations' cost and the
    // effective routing all show what was just saved.
    try {
      await refreshAssembly(savedId, { keepEdits: false });
    } catch {
      setAssemblyId(savedId);
      setSelectedAssembly(result);
      setLoadedAssemblyPrice(result.assembly_price ?? null);
      markHeaderSaved();
      if (opsSaved) markOpsSaved();
    }
  }

  async function handleDeleteAssembly() {
    if (!assemblyId) { alert("No assembly selected."); return; }
    if (!window.confirm("Are you sure you want to delete this assembly?")) return;
    try {
      const res = await fetch(`${API_BASE}/assemblies/${assemblyId}`, { method: "DELETE" });
      if (!res.ok) { const msg = await res.text(); throw new Error(`Delete failed: ${res.status} => ${msg}`); }
      const data = await res.json();
      alert(data.message);
      handleCreateNew();
      loadAssemblies();
    } catch (err) { setError(err.message); }
  }

  // ================================
  // Recipe Management Functions
  // ================================
  
  function handleAddRecipeItem() {
      if (!newRecipeItemId) return;
      const qty = parseInt(newRecipeItemQty, 10) || 1;
      
      let name = "";
      if (newRecipeItemType === 'Cut Sheet') {
          const cs = cutSheetList.find(s => s.cut_sheet_id === parseInt(newRecipeItemId));
          name = cs ? cs.cut_sheet_name : `Sheet #${newRecipeItemId}`;
      } else {
          const cl = cutListList.find(l => l.cut_list_id === parseInt(newRecipeItemId));
          name = cl ? cl.cut_list_name : `List #${newRecipeItemId}`;
      }

      setPendingRecipeItems(prev => [
          ...prev, 
          { type: newRecipeItemType, id: parseInt(newRecipeItemId), name, qty }
      ]);
      setNewRecipeItemId("");
      setNewRecipeItemQty("1");
  }

  function handleRemoveRecipeItem(index) {
      setPendingRecipeItems(prev => prev.filter((_, i) => i !== index));
  }

  async function handleSaveRecipe() {
      if (!assemblyId) { alert("Please save the assembly first."); return; }
      if (pendingRecipeItems.length === 0) { alert("Please add items to the recipe."); return; }
      
      const payload = {
          assembly_id: assemblyId,
          recipe_name: recipeName,
          is_default: isDefaultRecipe,
          items: pendingRecipeItems.map(item => ({
              cut_sheet_id: item.type === 'Cut Sheet' ? item.id : null,
              cut_list_id: item.type === 'Cut List' ? item.id : null,
              quantity_per_batch: item.qty
          }))
      };

      try {
          const res = await fetch(`${API_BASE}/assemblies/recipes`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });
          if (!res.ok) throw new Error(await apiErrorMessage(res));
          
          alert("Recipe saved successfully!");
          loadRecipes(assemblyId);
          loadYieldInfo(assemblyId);
      } catch (err) {
          setError(`Failed to save recipe: ${err.message}`);
      }
  }

  // ================================
  // Document Management
  // ================================

  // Re-read an assembly from the server. By default unsaved edits to the SAME
  // assembly survive (see editsToKeep); pass keepEdits:false after a save or
  // when the operator chose to discard them.
  async function refreshAssembly(id, { keepEdits = true } = {}) {
    const keep = keepEdits ? editsToKeep(id) : null;
    const res = await fetch(`${API_BASE}/assemblies/${id}`);
    if (!res.ok) throw new Error("Failed to refresh assembly data.");
    const freshAssembly = await res.json();
    handleSelectAssembly(freshAssembly, keep);
  }

  // Sidebar click / New / Discard: these replace the form, so ask first.
  function openAssemblyFromList(asm) {
    if (!confirmDiscard(`Opening #${asm.assembly_id} ${asm.assembly_name}`)) return;
    refreshAssembly(asm.assembly_id, { keepEdits: false }).catch(() => handleSelectAssembly(asm));
  }

  function startNewAssembly() {
    if (!confirmDiscard("Starting a new assembly")) return;
    handleCreateNew();
  }

  function discardChanges() {
    if (!window.confirm(`Discard your unsaved changes (${unsavedList.join(", ")}) and reload the saved assembly?`)) return;
    if (assemblyId) {
      refreshAssembly(assemblyId, { keepEdits: false })
        .catch(() => { if (selectedAssembly) handleSelectAssembly(selectedAssembly); });
    } else {
      handleCreateNew();
    }
  }

  // One-click cure: repoint this assembly's outdated part references (and the
  // safe open assembly-linked order lines) to the current revisions.
  async function handleApplyCurrentRevisions() {
    if (!assemblyId || !revStatus?.has_outdated) return;
    const eligible = (revStatus.template_refs || []).filter((r) => r.eligible);
    if (eligible.length === 0) {
      alert("Nothing is eligible to update — see the reasons listed in the warning banner.");
      return;
    }
    const rideAlong =
      (revStatus.so_lines || []).filter((l) => l.eligible).length +
      (revStatus.quote_lines || []).filter((l) => l.eligible).length;
    const summary = eligible
      .map((r) => `• ${r.product_id}: Rev ${r.old_revision_code} → Rev ${r.current_revision_code}${r.kind === "primary" ? " (primary part)" : ""}`)
      .join("\n");
    if (!window.confirm(
      `Update ${eligible.length} part reference(s) on this assembly to the current revision?\n\n${summary}` +
      (rideAlong
        ? `\n\nAlso updates ${rideAlong} open assembly-linked order line(s) in place (same order numbers, price untouched).`
        : "")
    )) return;
    const pin = await pinPrompt("Enter PIN to update this assembly to the current revisions:");
    if (!pin) return;
    setRevBusy(true);
    try {
      const res = await fetch(`${API_BASE}/assemblies/${assemblyId}/apply_current_revisions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(data.detail) || `HTTP ${res.status}`);
      let msg = data.message || "Updated.";
      const cutSheets = data.cut_sheets_on_old_rev || [];
      if (cutSheets.length > 0) {
        msg += `\n\n⚠ Cut sheet(s) still nest the OLD revision: ${cutSheets.map((cs) => cs.cut_sheet_name).join(", ")}.` +
               "\nImport the updated nest as a cut-sheet revision before cutting.";
      }
      const blockedLines = (data.blocked_so_lines || []).length + (data.blocked_quote_lines || []).length;
      if (blockedLines > 0) {
        msg += `\n\nℹ ${blockedLines} order line(s) stay on the old revision (production has begun or closed paperwork).`;
      }
      alert(msg);
      await refreshAssembly(assemblyId);
      loadAssemblies();
    } catch (e) {
      alert(`Update failed: ${e.message || e}`);
    } finally {
      setRevBusy(false);
    }
  }

  async function handleUploadAssemblyImage() {
    if (!assemblyImageFile) { alert("Please select an image file."); return; }
    if (!assemblyId) { alert("Please save the assembly before uploading an image."); return; }
    try {
      const formData = new FormData();
      formData.append("file", assemblyImageFile);
      const res = await fetch(`${API_BASE}/assemblies/${assemblyId}/upload-image`, { method: "POST", body: formData });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      alert("Assembly image uploaded!");
      setAssemblyImageFile(null);
      await refreshAssembly(assemblyId);
    } catch (err) { setError(err.message); }
  }

  async function handleFileUpload() {
    if (!fileToUpload) { alert("Please select a file to upload."); return; }
    if (!assemblyId) { alert("Please save the assembly before uploading documents."); return; }

    const pin = await pinPrompt("Please enter your PIN to confirm the upload:");
    if (!pin) return;

    setIsUploading(true);
    setError(null);

    try {
        const presignResponse = await fetch(`${API_BASE}/v1/s3/presigned-url?file_name=${encodeURIComponent(fileToUpload.name)}&file_type=${encodeURIComponent(fileToUpload.type)}&folder=Assemblies&assembly_id=${assemblyId}`, { method: 'POST' });
        if (!presignResponse.ok) throw new Error('Failed to get an upload URL.');
        const presignData = await presignResponse.json();

        const formData = new FormData();
        Object.keys(presignData.fields).forEach(key => formData.append(key, presignData.fields[key]));
        formData.append('file', fileToUpload);

        const s3Response = await fetch(presignData.url, { method: 'POST', body: formData });
        if (!s3Response.ok) throw new Error('Failed to upload file to S3.');

        const metadataPayload = { s3_key: presignData.s3_key, original_filename: fileToUpload.name, pin: pin };
        const dbResponse = await fetch(`${API_BASE}/assemblies/${assemblyId}/documents`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(metadataPayload),
        });
        if (!dbResponse.ok) throw new Error('Failed to save document metadata.');

        alert("File uploaded successfully!");
        setFileToUpload(null);
        await refreshAssembly(assemblyId);
    } catch (err) { setError(err.message); } finally { setIsUploading(false); }
  }

  async function handleDeleteDocument(doc) {
      if (!window.confirm(`Delete file "${doc.original_filename}"?`)) return;
      setError(null);
      try {
          const res = await fetch(`${API_BASE}/assemblies/${assemblyId}/documents/${doc.assembly_document_id}`, { method: 'DELETE' });
          if (!res.ok) throw new Error(await apiErrorMessage(res));
          alert("Document deleted.");
          await refreshAssembly(assemblyId);
      } catch (err) { setError(err.message); }
  }

  // ================================
  // Component Management
  // ================================
  async function handleAddComponent() {
    if (!assemblyId) { alert("Save the assembly first."); return; }
    if (!newCompProductId) { alert("Select a product ID."); return; }
    const quantity = parseInt(newCompQty, 10);
    if (!quantity || quantity < 1) { alert("Quantity must be >= 1."); return; }

    const foundPart = partsList.find((p) => p.product_id === newCompProductId.trim());
    if (!foundPart) { alert("Invalid product ID."); return; }

    // The primary is implicit (Primary Part Qty) — adding it as a component
    // double-counts it everywhere (the assembly-116 bug). Server enforces too.
    const primaryIsSame =
      foundPart.product_id === assemblyPrimaryPartProdId ||
      (selectedAssembly && selectedAssembly.primary_part_id === foundPart.part_id);
    if (primaryIsSame) {
      alert("That part is the assembly's PRIMARY part — it's already included via Primary Part Qty. Increase Primary Part Qty instead of adding it as a component.");
      return;
    }

    try {
      const payload = { part_id: foundPart.part_id, quantity_per_assembly: quantity };
      const res = await fetch(`${API_BASE}/assemblies/${assemblyId}/components`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload)
      });
      if (!res.ok) { const msg = await res.text(); throw new Error(`Add component failed: ${res.status} => ${msg}`); }
      alert(`Added component.`);
      // Reload from the server (header, primary_part, pricing) rather than
      // re-applying the selectedAssembly snapshot (F21).
      if (assemblyId) await refreshAssembly(assemblyId).catch(() => { if (selectedAssembly) handleSelectAssembly(selectedAssembly, editsToKeep(assemblyId)); });
      setNewCompProductId(""); setNewCompQty("1");
    } catch (err) { setError(err.message); }
  }

  async function handleRemoveComponent(comp) {
    if (!window.confirm(`Remove part_id=${comp.part_id}?`)) return;
    try {
      const res = await fetch(`${API_BASE}/assemblies/${comp.assembly_id}/components/${comp.assembly_component_id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      alert("Component removed.");
      // Reload from the server (header, primary_part, pricing) rather than
      // re-applying the selectedAssembly snapshot (F21).
      if (assemblyId) await refreshAssembly(assemblyId).catch(() => { if (selectedAssembly) handleSelectAssembly(selectedAssembly, editsToKeep(assemblyId)); });
    } catch (err) { setError(err.message); }
  }
  
  function beginEdit(comp) { setEditingCompId(comp.assembly_component_id); setEditingCompQty(String(comp.quantity_per_assembly)); }
  function cancelEdit() { setEditingCompId(null); setEditingCompQty(""); }

  async function handleSaveEdit(comp) {
    const quantity = parseInt(editingCompQty, 10);
    if (!quantity || quantity < 1) { alert("Quantity must be >= 1."); return; }
    try {
      const res = await fetch(`${API_BASE}/assemblies/${comp.assembly_id}/components/${comp.assembly_component_id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ quantity_per_assembly: quantity })
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      alert("Updated."); setEditingCompId(null);
      // Reload from the server (header, primary_part, pricing) rather than
      // re-applying the selectedAssembly snapshot (F21).
      if (assemblyId) await refreshAssembly(assemblyId).catch(() => { if (selectedAssembly) handleSelectAssembly(selectedAssembly, editsToKeep(assemblyId)); });
    } catch (err) { setError(err.message); }
  }

  // F06: a part whose stored cost_labor_material no longer equals its own
  // components (material-only PATCHes from the nester / MCP leave it that way)
  // is "stale". PATCH each stale part with its STORED components — never the
  // totals — so the server, which now owns the rollup formula, recomputes
  // cost_labor_material / price_labor_material; then reload.
  async function handleRecalculatePartCosts() {
    const stale = effectiveComponents.filter((c) => componentCostView(c).stale);
    if (stale.length === 0) { alert("No stale part rollups — every stored total already matches its components."); return; }
    const summary = stale.map((c) => {
      const v = componentCostView(c);
      return `• ${c.product_id}: stored ${fmtMoney(v.stored)} → live ${fmtMoney(v.live)} (${fmtDelta(v.delta)})`;
    }).join("\n");
    if (!window.confirm(
      `Recalculate ${stale.length} part rollup(s) on the server?\n\n${summary}\n\n` +
      "Each part is PATCHed with its stored cost components (labor, gas, consumables, material, coating — never the totals, " +
      "and not markup, which the server keeps as stored); the server recomputes cost_labor_material / price_labor_material from them."
    )) return;
    setRecalcBusy(true);
    const failures = [];
    try {
      for (const c of stale) {
        const payload = rollupPatchPayload(c);
        if (Object.keys(payload).length === 0) { failures.push(`${c.product_id}: row carries no component fields`); continue; }
        try {
          const res = await fetch(`${API_BASE}/parts/${c.part_id}`, {
            method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
          });
          if (!res.ok) throw new Error(await apiErrorMessage(res, `HTTP ${res.status}`));
        } catch (e) {
          failures.push(`${c.product_id}: ${e.message}`);
        }
      }
      if (assemblyId) await refreshAssembly(assemblyId).catch(() => {});
      alert(
        (failures.length
          ? `Recalculated ${stale.length - failures.length} of ${stale.length}.\n\nFailed:\n${failures.join("\n")}`
          : `Recalculated ${stale.length} part rollup(s).`) +
        "\n\nIf a row is still flagged stale after the reload, this API does not yet recompute rollups on PATCH /parts (spec B1) — the stored total was written back unchanged."
      );
    } finally {
      setRecalcBusy(false);
    }
  }

  async function handleAddConsumableComponent() {
    if (!assemblyId) { alert("Save assembly first."); return; }
    const consumableId = parseInt(newConsumableId, 10);
    const quantity = parseInt(newConsumableQty, 10);
    if (!consumableId) { alert("Select a component."); return; }
    if (!quantity || quantity < 1) { alert("Quantity >= 1."); return; }
    try {
      const res = await fetch(`${API_BASE}/assemblies/${assemblyId}/consumable-components`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ consumable_id: consumableId, quantity_per_assembly: quantity }),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      alert("Component added."); setNewConsumableId(""); setNewConsumableQty("1");
      // Reload from the server (header, primary_part, pricing) rather than
      // re-applying the selectedAssembly snapshot (F21).
      if (assemblyId) await refreshAssembly(assemblyId).catch(() => { if (selectedAssembly) handleSelectAssembly(selectedAssembly, editsToKeep(assemblyId)); });
    } catch (err) { setError(err.message); }
  }

  async function handleRemoveConsumableComponent(cons) {
    if (!window.confirm(`Remove "${cons.product_id}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/assemblies/${assemblyId}/consumable-components/${cons.assembly_consumable_id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      alert("Removed.");
      // Reload from the server (header, primary_part, pricing) rather than
      // re-applying the selectedAssembly snapshot (F21).
      if (assemblyId) await refreshAssembly(assemblyId).catch(() => { if (selectedAssembly) handleSelectAssembly(selectedAssembly, editsToKeep(assemblyId)); });
    } catch (err) { setError(err.message); }
  }
  
  function beginEditConsumable(cons) { setEditingConsumableId(cons.assembly_consumable_id); setEditingConsumableQty(String(cons.quantity_per_assembly)); setEditingConsumableLoose(!!cons.ships_loose); }
  function cancelEditConsumable() { setEditingConsumableId(null); setEditingConsumableQty(""); }

  async function handleSaveEditConsumable(cons) {
    const quantity = parseInt(editingConsumableQty, 10);
    if (!quantity || quantity < 1) { alert("Quantity >= 1."); return; }
    try {
      const res = await fetch(`${API_BASE}/assemblies/${assemblyId}/consumable-components/${cons.assembly_consumable_id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ quantity_per_assembly: quantity, ships_loose: !!editingConsumableLoose }),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      alert("Updated."); setEditingConsumableId(null);
      // Reload from the server (header, primary_part, pricing) rather than
      // re-applying the selectedAssembly snapshot (F21).
      if (assemblyId) await refreshAssembly(assemblyId).catch(() => { if (selectedAssembly) handleSelectAssembly(selectedAssembly, editsToKeep(assemblyId)); });
    } catch (err) { setError(err.message); }
  }

  function handleEnlarge(url) { if (!url) return; setEnlargedImage(url); }
  function closeEnlarge() { setEnlargedImage(null); }

  // ================================
  // Render
  // ================================
  return (
    <div className="d-flex" style={{ height: "100vh" }}>
      {/* Left Panel: List of assemblies with sorting/filtering */}
      <div className="border-end p-3" style={{ width: "300px", flexShrink: 0, overflowY: "auto" }}>
        <div className="mb-2 d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Assemblies</h5>
          <button className="btn btn-sm btn-outline-primary" onClick={startNewAssembly}>New</button>
        </div>

        {/* --- Sidebar Filters --- */}
        <div className="mb-2">
            <label className="form-label">Sort By</label>
            <select className="form-select form-select-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name_asc">Name (A-Z)</option>
                <option value="name_desc">Name (Z-A)</option>
                <option value="date_newest">Newest Created</option>
                <option value="date_oldest">Oldest Created</option>
            </select>
        </div>

        <div className="mb-2">
            <label className="form-label">Filter by Customer</label>
            <select className="form-select form-select-sm" value={filterCustomerId} onChange={(e) => setFilterCustomerId(e.target.value)}>
                <option value="">All</option>
                {customersList.map((cust) => <option key={cust.customer_id} value={cust.customer_id}>{cust.name}</option>)}
            </select>
        </div>

        <input type="text" className="form-control form-control-sm mb-2" placeholder="Search by Name or ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

        <div className="form-check form-check-sm mb-3">
          <input className="form-check-input" type="checkbox" id="asmShowInactive"
                 checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          <label className="form-check-label small" htmlFor="asmShowInactive">
            Show inactive{inactiveCount > 0 && <span className="text-muted"> ({inactiveCount})</span>}
          </label>
        </div>
        
        {loading && <div>Loading...</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        
        {!loading && filteredAndSortedAssemblies.map((asm) => (
            <div key={asm.assembly_id} className={`p-2 border-bottom ${selectedAssembly && selectedAssembly.assembly_id === asm.assembly_id ? "bg-primary-subtle" : "bg-white"}`} style={{ cursor: "pointer" }} onClick={() => openAssemblyFromList(asm)}>
              <div className="fw-bold">
                #{asm.assembly_id}: {asm.assembly_name}
                {asm.has_superseded_refs && (
                  <span className="ms-1" title="References a superseded part revision — open to review and update">⚠️</span>
                )}
                {asm.active_status === false && (
                  <span className="badge bg-secondary ms-2" title="Retired by the stale-catalog sweep — open it to reactivate">Inactive</span>
                )}
              </div>
            </div>
        ))}
        {!loading && filteredAndSortedAssemblies.length === 0 && (
            <div className="text-muted small text-center mt-2">No assemblies found.</div>
        )}
      </div>

      {/* Right Panel: assembly form & components */}
      <div className="flex-grow-1 p-4 overflow-auto">
        {error && <div className="alert alert-danger">{error}</div>}

        {/* Sticky while scrolling: an edit anywhere on this long form (most
            easily the operations grid) is named here until it is saved. */}
        {hasUnsaved && (
          <div className="alert alert-warning d-flex justify-content-between align-items-center py-2 mb-3 shadow-sm"
               style={{ position: "sticky", top: 0, zIndex: 1020 }} role="status" data-testid="unsaved-bar">
            <div>
              <strong>Unsaved changes:</strong> {unsavedList.join(", ")}
              <div className="small">Nothing is saved until you press {assemblyId ? "Save Changes" : "Create Assembly"}. It saves the assembly and its operations together.</div>
            </div>
            <div className="text-nowrap ms-3">
              <button className="btn btn-sm btn-outline-secondary me-2" onClick={discardChanges} disabled={saving}>Discard</button>
              <button className="btn btn-sm btn-primary" onClick={handleSaveAll} disabled={saving}>
                {saving ? "Saving…" : (assemblyId ? "Save Changes" : "Create Assembly")}
              </button>
            </div>
          </div>
        )}

        <h4>{assemblyId ? `Editing Assembly #${assemblyId}` : "Create New Assembly"}</h4>

        {/* The sweep retires an assembly together with its primary part. While
            that part is inactive it is absent from partsList, so the Primary
            Part dropdown renders blank and the save guard below refuses every
            edit — the assembly is effectively read-only. Surface that plainly
            and offer the one-click way out, rather than leaving an operator
            staring at an empty required field. */}
        {selectedAssembly && (selectedAssembly.active_status === false ||
          (selectedAssembly.primary_part_id && !assemblyPrimaryPartProdId)) && (
          <div className="alert alert-warning d-flex justify-content-between align-items-center py-2">
            <div>
              <strong>
                {selectedAssembly.active_status === false
                  ? "This assembly is inactive."
                  : "This assembly's primary part is inactive."}
              </strong>
              <div className="small">
                Retired by the stale-catalog sweep (no job in 12 months). The Primary Part
                dropdown stays blank and changes cannot be saved until it is restored.
              </div>
            </div>
            <button
              type="button"
              className="btn btn-warning btn-sm text-nowrap ms-3"
              onClick={() => reactivateAssembly(selectedAssembly.assembly_id)}
            >
              Reactivate assembly + parts
            </button>
          </div>
        )}

        {/* --- SUPERSEDED-REVISION WARNING + ONE-CLICK CURE --- */}
        {assemblyId && revStatus?.has_outdated && (
          <div className="alert alert-warning border-warning mb-3">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div style={{ minWidth: "60%" }}>
                <strong>⚠ This assembly references superseded part revisions</strong>
                <ul className="mb-1 mt-1 small">
                  {(revStatus.template_refs || []).map((r) => (
                    <li key={`${r.kind}-${r.assembly_component_id || r.assembly_id}`}>
                      <strong>{r.product_id}</strong>{r.kind === "primary" ? " (primary part)" : ""}:{" "}
                      Rev {r.old_revision_code} → current Rev {r.current_revision_code}
                      {!r.eligible && <span className="text-danger"> — {r.reason}</span>}
                    </li>
                  ))}
                </ul>
                <div className="small text-muted">
                  {(() => {
                    const ride = (revStatus.so_lines || []).filter((l) => l.eligible).length +
                                 (revStatus.quote_lines || []).filter((l) => l.eligible).length;
                    const blocked = (revStatus.so_lines || []).filter((l) => !l.eligible).length +
                                    (revStatus.quote_lines || []).filter((l) => !l.eligible).length;
                    return (
                      <>
                        {ride > 0 && <div>{ride} open assembly-linked order line(s) will be updated in place along with the fix (same order numbers, price untouched).</div>}
                        {blocked > 0 && <div>{blocked} order line(s) stay on the old revision — production has begun or the paperwork is closed.</div>}
                      </>
                    );
                  })()}
                  {(revStatus.cut_sheets_on_old_rev || []).length > 0 && (
                    <div>
                      ⚠ Cut sheets still nesting the old revision:{" "}
                      {(revStatus.cut_sheets_on_old_rev || []).map((cs) => cs.cut_sheet_name).join(", ")}
                      {" — import the updated nest as a cut-sheet revision before cutting."}
                    </div>
                  )}
                </div>
              </div>
              <button
                className="btn btn-warning fw-bold"
                disabled={revBusy || (revStatus.template_refs || []).every((r) => !r.eligible)}
                onClick={handleApplyCurrentRevisions}
                title="Repoint this assembly's outdated part references (and safe open order lines) to the current revision"
              >
                {revBusy ? "Updating…" : "Update to current revisions"}
              </button>
            </div>
          </div>
        )}

        {/* Assembly Header Form */}
        {/* ROW 1: ID, Name, Customer, Build Sets */}
        <div className="row mb-3">
          <div className="col-md-1">
            <label className="form-label">ID</label>
            <input type="text" className="form-control" style={{ backgroundColor: "#e9ecef" }} value={assemblyId || ""} readOnly />
          </div>
          <div className="col-md-4">
            <label className="form-label">Assembly Name</label>
            <input type="text" className="form-control" value={assemblyName} onChange={(e) => setAssemblyName(e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Customer (Owner)</label>
            <select className="form-select" value={assemblyCustomerId} onChange={(e) => { setAssemblyCustomerId(e.target.value); setComponentPartCustomerFilter(e.target.value); }}>
                <option value="">-- None / Internal --</option>
                {customersList.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
            </select>
          </div>
          <div className="col-md-3">
             <label className="form-label">Build in Sets of</label>
             <input type="number" className="form-control" value={buildSetQuantity} onChange={(e) => setBuildSetQuantity(e.target.value)} placeholder="e.g., 2 (optional)" min="1" />
          </div>
        </div>

        {/* ROW 2: Primary Part Selection */}
        <div className="row mb-3">
          <div className="col-md-3">
            <label className="form-label text-primary">Filter Primary by Customer</label>
            <select 
                className="form-select border-primary" 
                value={primaryPartCustomerFilter} 
                onChange={(e) => setPrimaryPartCustomerFilter(e.target.value)}
            >
                <option value="">-- All Customers --</option>
                {customersList.map(c => (
                    <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Primary Part (Required)</label>
            <select className="form-select" value={assemblyPrimaryPartProdId} onChange={(e) => setAssemblyPrimaryPartProdId(e.target.value)} required>
              <option value="">-- Select Primary Part --</option>
              {filteredPrimaryParts.map((p) => (<option key={p.part_id} value={p.product_id}>{p.product_id} - {p.description}</option>))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Primary Part Qty</label>
            <input type="number" className="form-control" value={primaryPartQuantity} onChange={(e) => setPrimaryPartQuantity(parseInt(e.target.value, 10) || 1)} min="1" disabled={!assemblyPrimaryPartProdId} />
          </div>
        </div>
        
        {/* ROW 3: Notes & Join Station */}
        <div className="row mb-3">
            <div className="col-md-6">
                <label className="form-label">Notes</label>
                <textarea className="form-control" value={assemblyNotes} onChange={(e) => setAssemblyNotes(e.target.value)} rows={3} />
            </div>
            <div className="col-md-3">
                <label className="form-label">Join Station (Required)</label>
                <select className="form-select" value={joinWorkstation} onChange={(e) => setJoinWorkstation(e.target.value)} required>
                <option value="">-- Choose Station --</option>
                <option value="Laser">Laser</option>
                <option value="Tap">Tap</option>
                <option value="Brake">Brake</option>
                <option value="Fabrication">Fabrication</option>
                <option value="Blast">Blast</option>
                <option value="Finish">Finish</option>
                <option value="Shipping">Shipping</option>
                </select>
            </div>
            {joinWorkstation === 'Fabrication' && (
                <div className="col-md-3">
                    <label className="form-label">Join Sub-Process (Required)</label>
                    <select className="form-select" value={joinSubProcess} onChange={(e) => setJoinSubProcess(e.target.value)} required>
                        <option value="">-- Choose Sub-Process --</option>
                        {FABRICATION_SUB_PROCESSES.map(sub => (<option key={sub} value={sub}>{sub}</option>))}
                    </select>
                </div>
            )}
        </div>

        {/* ROW 3.5: Assembly Operations — per-ASSEMBLY at/after-join work
            (assembly_processes). These rows drive job tasks, scheduling
            estimates, and the quote's "Assembly operations" line. Enter
            per-assembly numbers verbatim — never divided by Primary Part Qty. */}
        <div className="card mb-3 border-info">
          <div className="card-header bg-info bg-opacity-25 fw-bold py-2 d-flex justify-content-between align-items-center">
            <span>
              <i className="bi bi-tools me-2"></i>Assembly Operations (at/after the join) — per assembly
              {opsChanged && <span className="badge bg-warning text-dark ms-2" data-testid="ops-unsaved-badge">unsaved</span>}
            </span>
            {/* Same action as the Save button at the bottom: one save writes the
                assembly AND its operations. */}
            <button className="btn btn-sm btn-info text-white" onClick={handleSaveAll} disabled={saving}>
              {saving ? "Saving…" : (assemblyId ? "Save Changes" : "Create Assembly")}
            </button>
          </div>
          <div className="card-body py-2">
              <>
                {processesMsg && (
                  <div className={`alert alert-${processesMsg.type} py-1 px-2 mb-2 small`}>{processesMsg.text}</div>
                )}
                <table className="table table-sm align-middle mb-2">
                  <thead>
                    <tr>
                      <th style={{ width: "3rem" }}>#</th>
                      <th style={{ width: "14rem" }}>Step</th>
                      <th>Time / Cost (per assembly)</th>
                      <th style={{ width: "9rem" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {processRows.map((r, idx) => (
                      <tr key={idx}>
                        <td>
                          {r.step && r.step === effectiveJoinStep
                            ? <span className="badge bg-primary" title="The join — where components come together">JOIN</span>
                            : <span className="text-muted">{idx + 1}</span>}
                        </td>
                        <td>
                          <select className="form-select form-select-sm" value={r.step}
                                  onChange={e => updateProcessRow(idx, "step", e.target.value)}>
                            <option value="">-- Step --</option>
                            {ASSEMBLY_OP_STEPS.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td>
                          {r.step === "Coating" ? (
                            <div className="d-flex gap-2">
                              <div className="input-group input-group-sm" style={{ maxWidth: "11rem" }}>
                                <span className="input-group-text">$</span>
                                <input type="number" step="0.01" min="0" className="form-control" placeholder="cost / assembly"
                                       value={r.coatCost} onChange={e => updateProcessRow(idx, "coatCost", e.target.value)} />
                              </div>
                              <select className="form-select form-select-sm" style={{ maxWidth: "16rem" }} value={r.coatVendor}
                                      onChange={e => updateProcessRow(idx, "coatVendor", e.target.value)}>
                                <option value="">-- Coating vendor --</option>
                                {vendorsList.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
                              </select>
                              {/* Requested finish color. An assembly is coated as one
                                  unit, so this wins over the primary part's color. */}
                              <div style={{ minWidth: "17rem" }}>
                                <CoatingColorField
                                  code={r.coatColorCode || ''}
                                  name={r.coatColorName || ''}
                                  hex={r.coatColorHex || ''}
                                  showLabel={false}
                                  onChange={(patch) => setProcessRows(rows => rows.map((row, i) => (
                                    i === idx ? {
                                      ...row,
                                      coatColorCode: patch.coating_color_code || '',
                                      coatColorName: patch.coating_color_name || '',
                                      coatColorHex: patch.coating_color_hex || '',
                                    } : row
                                  )))}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="input-group input-group-sm" style={{ maxWidth: "13rem" }}>
                              <input type="number" step="0.5" min="0" className="form-control" placeholder="minutes"
                                     value={r.minutes} onChange={e => updateProcessRow(idx, "minutes", e.target.value)} />
                              <span className="input-group-text">min / assembly</span>
                            </div>
                          )}
                        </td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-outline-secondary py-0 me-1" title="Move up"
                                  onClick={() => moveProcessRow(idx, -1)} disabled={idx === 0}>↑</button>
                          <button className="btn btn-sm btn-outline-secondary py-0 me-1" title="Move down"
                                  onClick={() => moveProcessRow(idx, 1)} disabled={idx === processRows.length - 1}>↓</button>
                          <button className="btn btn-sm btn-outline-danger py-0" title="Remove"
                                  onClick={() => removeProcessRow(idx)}>✕</button>
                        </td>
                      </tr>
                    ))}
                    {processRows.length === 0 && (
                      <tr>
                        <td colSpan="4" className="small">
                          <span className="text-danger fw-bold">No operations defined</span>
                          <span className="text-muted"> — jobs from this assembly will create no join task. Add the join step at minimum.</span>
                          {effectiveJoinStep && ASSEMBLY_OP_STEPS.includes(effectiveJoinStep) && (
                            <button className="btn btn-sm btn-outline-primary py-0 ms-2"
                                    onClick={() => setProcessRows([{ step: effectiveJoinStep, minutes: "", coatCost: "", coatVendor: "",
                                                                    coatColorCode: "", coatColorName: "", coatColorHex: "", notes: "" }])}>
                              + Add the {effectiveJoinStep} join step
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <button className="btn btn-sm btn-outline-info" onClick={addProcessRow}>+ Add operation</button>
                <span className="text-muted small ms-3">
                  Saved with the rest of the assembly by <strong>Save Changes</strong> (here or at the bottom of the page). Part-level routing for these steps is managed here, not on the part.
                </span>

                {/* Costed operations (F22): ties the minutes above to the dollars
                    the card prints — station | minutes | cost rate | cost |
                    price rate | price. Saved rows come from
                    POST /quotes/calculate-assembly-ops (the same call the quote
                    line prices from); while the grid is dirty the standard-rate
                    estimate is shown and named as such. */}
                {opsTableRows.length > 0 && (
                  <div className="mt-3">
                    <div className="small fw-bold mb-1">
                      Costed operations — per assembly ·{' '}
                      {opsIsEstimate
                        ? <span className="badge bg-warning text-dark fw-normal">unsaved — standard rate estimate (wage table; no sell price until saved)</span>
                        : <span className="fw-normal">saved rows priced by POST /quotes/calculate-assembly-ops at {DEFAULT_RATE_SET_NAME}</span>}
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-1" style={{ maxWidth: "72rem" }}>
                        <thead className="table-light small">
                          <tr>
                            <th>Station</th>
                            <th className="text-end">Minutes</th>
                            <th className="text-end">Cost rate <span className="fw-normal text-muted">(wages, 90-day)</span></th>
                            <th className="text-end">Cost</th>
                            <th className="text-end">Price rate <span className="fw-normal text-muted">({DEFAULT_RATE_SET_NAME})</span></th>
                            <th className="text-end">Price</th>
                            <th>Source</th>
                          </tr>
                        </thead>
                        <tbody className="small">
                          {opsTableRows.map((r, i) => (
                            <tr key={i}>
                              <td>{r.station}</td>
                              <td className="text-end">{r.minutes === null ? <span className="text-muted">— (outsourced $)</span> : fmtMinutes(r.minutes)}</td>
                              <td className="text-end" title={r.costRateSource || ""}>
                                {r.isOutsourced ? <span className="text-muted">typed $</span> : fmtRate(r.costRate)}
                                {!r.isOutsourced && r.costRateSource && <div className="text-muted" style={{ fontSize: "0.7rem" }}>{r.costRateSource}</div>}
                              </td>
                              <td className="text-end" title={r.isOutsourced ? "vendor dollar amount on the row" : `${fmtMinutes(r.minutes)} ÷ 60 × ${fmtRate(r.costRate)}`}>{fmtMoney(r.cost)}</td>
                              <td className="text-end" title={r.priceRateSource || ""}>
                                {r.isOutsourced
                                  ? (r.markup === null ? <span className="text-muted">—</span> : <>× (1 + {fmtPct(r.markup, 0)})</>)
                                  : fmtRate(r.priceRate)}
                                {!r.isOutsourced && r.priceRateSource && <div className="text-muted" style={{ fontSize: "0.7rem" }}>{r.priceRateSource}</div>}
                              </td>
                              <td className="text-end" title={r.price === null ? "priced only once the rows are saved" : (r.isOutsourced ? `${fmtMoney(r.cost)} × (1 + markup)` : `${fmtMinutes(r.minutes)} ÷ 60 × ${fmtRate(r.priceRate)}`)}>
                                {fmtMoney(r.price, { dash: true })}
                              </td>
                              <td>
                                <span className={sourceBadgeClass(r.source)}>
                                  {r.source === 'historical' ? `historical${r.historyN ? ` n=${r.historyN}` : ''}`
                                    : r.source === 'assembly_row' ? 'assembly row' : r.source}
                                </span>
                                {r.costRateSource && r.costRateSource.startsWith('default') && (
                                  <span className={`${sourceBadgeClass('default')} ms-1`}>default rate</span>
                                )}
                                {!r.isOutsourced && !r.hasProvenance && !opsIsEstimate && (
                                  <span className="text-muted ms-1" title="The calculator did not label this row's rates; the $/hr shown are implied from cost ÷ hours and price ÷ hours">rates implied</span>
                                )}
                              </td>
                            </tr>
                          ))}
                          <tr className="fw-bold">
                            <td>Total</td>
                            <td></td>
                            <td></td>
                            <td className="text-end">{fmtMoney(cardTotals.operations.cost)}</td>
                            <td></td>
                            <td className="text-end">{fmtMoney(cardTotals.operations.price, { dash: true })}</td>
                            <td className="fw-normal text-muted">
                              cost = Σ minutes ÷ 60 × cost rate + outsourced $ · price = Σ minutes ÷ 60 × price rate + outsourced $ × (1 + markup)
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Price rates: {rateSetLabel(cardModel.rates_used)}. Cost rates are employee wages (90-day punch-weighted; default wage table where a station has no punches) — machine burden is not in cost.
                    </div>
                  </div>
                )}
              </>
          </div>
        </div>

        {/* ROW 3.6: Effective Routing — the RESOLVED chain (primary part's
            upstream steps + the operations above). This is the only place the
            two halves appear together; before it existed, a wrong order was
            invisible until a job exploded into tasks. Reloads on Save
            Changes. */}
        {assemblyId && effChain && (
          <div className="card mb-3 border-secondary">
            <div className="card-header bg-secondary bg-opacity-10 fw-bold py-2">
              <i className="bi bi-signpost-split me-2"></i>Effective Routing — what jobs will actually run
            </div>
            <div className="card-body py-2">
              <div className="d-flex flex-wrap align-items-center gap-1 mb-2">
                {effChain.steps.map((s, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="text-muted">→</span>}
                    <span
                      className={`badge ${s.is_join ? "bg-primary" : s.source === "assembly" ? "bg-info text-dark" : "bg-light text-dark border"}`}
                      title={`${s.source === "assembly" ? "From this assembly's operations" : "From the primary part's process sequence"}`}
                    >
                      {s.step}
                    </span>
                  </React.Fragment>
                ))}
                <span className="text-muted">→</span>
                <span className="badge bg-light text-dark border">Shipping</span>
              </div>
              <table className="table table-sm table-borderless mb-2" style={{ maxWidth: "44rem" }}>
                <thead>
                  <tr className="text-muted small">
                    <th style={{ width: "2rem" }}>#</th><th>Step</th><th>Defined on</th><th>Batching</th>
                  </tr>
                </thead>
                <tbody>
                  {effChain.steps.map((s, i) => (
                    <tr key={i} className="small">
                      <td className="text-muted">{s.position}</td>
                      <td>
                        {s.step}
                        {s.is_join && <span className="badge bg-primary ms-2">JOIN</span>}
                      </td>
                      <td className="text-muted">
                        {s.source === "assembly"
                          ? "this assembly"
                          : <>the part <span className="font-monospace">{effChain.primary_product_id}</span></>}
                      </td>
                      <td className="text-muted">{s.batching}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(effChain.warnings || []).map((w, i) => (
                <div key={i} className="alert alert-warning py-1 px-2 mb-1 small">
                  <i className="bi bi-exclamation-triangle me-1"></i>{w}
                </div>
              ))}
              <div className="text-muted small">
                Primary part <span className="font-monospace">{effChain.primary_product_id}</span> contributes{" "}
                <span className="font-monospace">{effChain.primary_process_steps || "(no steps)"}</span>; steps at or after the
                join come from the operations above. Save Changes to refresh.
              </div>
            </div>
          </div>
        )}

        {/* ROW 4: Shipping Profile — final assembled-product dims/weight.
            The packaging planner packs this assembly as ONE shipping unit of
            these dimensions (pallet-direct stacks for big weldments, cartons
            for small ones). Without dims it falls back to the primary part's
            dims and the SO packaging plan shows a warning. */}
        <div className="card mb-3 border-warning">
          <div className="card-header bg-warning bg-opacity-25 fw-bold py-2">
            <i className="bi bi-box-seam me-2"></i>Shipping Profile (finished assembly)
            {!(shipLengthIn && shipWidthIn && shipHeightIn) && (
              <span className="badge bg-warning text-dark ms-2" title="Packaging plans fall back to the primary part's dims until these are set — layouts for welded assemblies will be wrong.">
                dims not set
              </span>
            )}
          </div>
          <div className="card-body py-2">
            <div className="row g-2 align-items-end">
              <div className="col-md-2">
                <label className="form-label mb-1">Length (in)</label>
                <input type="number" min="0" step="0.125" className="form-control" value={shipLengthIn}
                       onChange={(e) => setShipLengthIn(e.target.value)} placeholder="e.g., 66" />
              </div>
              <div className="col-md-2">
                <label className="form-label mb-1">Width (in)</label>
                <input type="number" min="0" step="0.125" className="form-control" value={shipWidthIn}
                       onChange={(e) => setShipWidthIn(e.target.value)} placeholder="e.g., 54" />
              </div>
              <div className="col-md-2">
                <label className="form-label mb-1">Height (in)</label>
                <input type="number" min="0" step="0.125" className="form-control" value={shipHeightIn}
                       onChange={(e) => setShipHeightIn(e.target.value)} placeholder="e.g., 5" />
              </div>
              <div className="col-md-2">
                <label className="form-label mb-1">Ship Weight (lb)</label>
                <input type="number" min="0" step="0.1" className="form-control" value={shipWeightLbs}
                       onChange={(e) => setShipWeightLbs(e.target.value)}
                       placeholder={assemblyWeight ? `BOM: ${Number(assemblyWeight).toFixed(1)}` : "BOM weight"} />
                <div className="form-text">Blank = BOM-summed weight{assemblyWeight ? ` (${Number(assemblyWeight).toFixed(1)} lb)` : ""}</div>
              </div>
              <div className="col-md-2">
                <div className="form-check mt-4">
                  <input className="form-check-input" type="checkbox" id="shipStackableChk"
                         checked={shipStackable} onChange={(e) => setShipStackable(e.target.checked)} />
                  <label className="form-check-label" htmlFor="shipStackableChk">Stackable</label>
                </div>
              </div>
              <div className="col-md-2">
                <label className="form-label mb-1">Max Stack Layers</label>
                <input type="number" min="1" className="form-control" value={shipMaxStackLayers}
                       onChange={(e) => setShipMaxStackLayers(e.target.value)}
                       placeholder="auto" disabled={!shipStackable} />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <button className="btn btn-primary me-2" onClick={handleSaveAll} disabled={saving}>
            {saving ? "Saving…" : (assemblyId ? "Save Changes" : "Create Assembly")}
          </button>
          {/* Retire, between Save and Delete: it is the reversible answer to
              "we are not building this any more", which is what people reach
              for Delete to say. Hidden once inactive — the banner above owns
              the other direction. */}
          {assemblyId && selectedAssembly?.active_status !== false && (
            <button className="btn btn-outline-secondary me-2" disabled={archiving}
                    onClick={() => archiveAssembly(assemblyId)}
                    title="Hide this assembly from the job, quote and part pickers. Parts are not changed; Reactivate restores it.">
              {archiving ? "Working…" : "Mark Inactive"}
            </button>
          )}
          {assemblyId && <button className="btn btn-outline-danger" onClick={handleDeleteAssembly}>Delete Assembly</button>}
        </div>

        <hr />

        {assemblyId && (
          <>
            {/* NEW: PRODUCTION RECIPE SECTION */}
            <div className="card mb-4 border-info">
              <div className="card-header bg-info text-white fw-bold">
                <i className="bi bi-tools me-2"></i> Production Recipe
              </div>
              <div className="card-body">
                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label fw-bold">Active Recipe</label>
                        <select className="form-select" value={recipeName} disabled={!assemblyId} onChange={() => {}}>
                            <option value="Standard Build">Standard Build (Default)</option>
                            {/* Future: Iterate recipes state to show options */}
                        </select>
                    </div>
                    <div className="col-md-6 d-flex align-items-end">
                        {yieldInfo && yieldInfo.batch_size > 0 ? (
                            <div className="alert alert-success py-1 px-3 mb-0 w-100">
                                <strong>Natural Batch Yield:</strong> {yieldInfo.batch_size} units per run
                            </div>
                        ) : (
                            <div className="alert alert-secondary py-1 px-3 mb-0 w-100">
                                Recipe not yet defined.
                            </div>
                        )}
                    </div>
                </div>

                <table className="table table-sm table-striped border">
                    <thead className="table-light">
                        <tr>
                            <th style={{width: '20%'}}>Type</th>
                            <th style={{width: '50%'}}>Source (Sheet / List)</th>
                            <th style={{width: '20%'}}>Qty Per Batch</th>
                            <th style={{width: '10%'}}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pendingRecipeItems.map((item, idx) => (
                            <tr key={idx}>
                                <td>{item.type}</td>
                                <td>{item.name}</td>
                                <td>{item.qty}</td>
                                <td><button className="btn btn-sm btn-danger py-0" onClick={() => handleRemoveRecipeItem(idx)}>X</button></td>
                            </tr>
                        ))}
                        <tr>
                            <td>
                                <select className="form-select form-select-sm" value={newRecipeItemType} onChange={e => setNewRecipeItemType(e.target.value)}>
                                    <option value="Cut Sheet">Cut Sheet</option>
                                    <option value="Cut List">Cut List</option>
                                </select>
                            </td>
                            <td>
                                <select className="form-select form-select-sm" value={newRecipeItemId} onChange={e => setNewRecipeItemId(e.target.value)}>
                                    <option value="">-- Select --</option>
                                    {newRecipeItemType === 'Cut Sheet' ? 
                                        cutSheetList.map(cs => <option key={cs.cut_sheet_id} value={cs.cut_sheet_id}>{cs.cut_sheet_name}</option>) :
                                        cutListList.map(cl => <option key={cl.cut_list_id} value={cl.cut_list_id}>{cl.cut_list_name}</option>)
                                    }
                                </select>
                            </td>
                            <td>
                                <input type="number" className="form-control form-control-sm" min="1" value={newRecipeItemQty} onChange={e => setNewRecipeItemQty(e.target.value)} />
                            </td>
                            <td>
                                <button className="btn btn-sm btn-success py-0" onClick={handleAddRecipeItem} disabled={!newRecipeItemId}>Add</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className="d-flex justify-content-end">
                    <button className="btn btn-primary btn-sm" onClick={handleSaveRecipe}>Save Recipe Definition</button>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 mb-1">
              <h5 className="mb-0">Parts for Assembly #{assemblyId}</h5>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted" style={{ fontSize: "0.75rem", maxWidth: "36rem" }}>
                  <strong>Stored</strong> = parts.cost_labor_material as saved · <strong>Live</strong> = labor + gas + consumables + material + coating
                  from the stored components (the server formula) · all per unit; the primary row is ppq × unit.
                </span>
                <button type="button" className="btn btn-sm btn-outline-warning text-nowrap"
                        onClick={handleRecalculatePartCosts}
                        disabled={recalcBusy || staleComponentCount === 0}
                        title={staleComponentCount === 0
                          ? "Every stored total matches its components (or the API sends no components to check against)"
                          : `${staleComponentCount} part(s) whose stored total ≠ their components — PATCH each with its stored components so the server recomputes the rollup`}>
                  {recalcBusy ? "Recalculating…" : `Recalculate part costs${staleComponentCount ? ` (${staleComponentCount} stale)` : ""}`}
                </button>
              </div>
            </div>
            {effectiveComponents.length === 0 ? <div>No parts attached.</div> : (
              <div className="table-responsive">
              <table className="table table-sm table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Part</th>
                    <th className="text-end">Qty</th>
                    <th className="text-end">Material</th>
                    <th className="text-end">Labor</th>
                    <th className="text-end">Gas + cons.</th>
                    <th className="text-end">Coating</th>
                    <th className="text-end">Stored total</th>
                    <th className="text-end">Live total</th>
                    <th className="text-end">Book price</th>
                    <th className="text-end">Cost subtotal <span className="fw-normal text-muted">(live)</span></th>
                    <th className="text-end">Price subtotal <span className="fw-normal text-muted">(book)</span></th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {effectiveComponents.map((comp) => {
                    const isEditing = editingCompId === comp.assembly_component_id;
                    const v = componentCostView(comp);
                    const qty = comp.quantity_per_assembly || 0;
                    const unitForSubtotal = v.live !== null ? v.live : v.stored;
                    const costSubtotal = unitForSubtotal === null ? null : qty * unitForSubtotal;
                    const priceSubtotal = v.bookPrice ? qty * v.bookPrice : null;
                    const coatingOnOps = comp.is_primary && v.coating > 0 && cardModel.operations.has_coating_row;

                    return (
                      <tr key={comp.assembly_component_id} className={comp.is_primary ? 'table-info' : ''}>
                        <td>
                          <div>
                            <strong>{comp.product_id}</strong> {comp.is_primary && '(Primary)'}
                            {(() => {
                              // Per-row revision badge: yellow when the row references a
                              // superseded revision, green when it's confirmed current —
                              // positive feedback either way, not just absence-of-warning.
                              const ref = (revStatus?.template_refs || []).find((r) => r.old_part_id === comp.part_id);
                              if (ref) {
                                return (
                                  <span className="badge bg-warning text-dark ms-2" title={`Current revision is Rev ${ref.current_revision_code}${ref.eligible ? " — use 'Update to current revisions' above" : ` — ${ref.reason}`}`}>
                                    Rev {ref.old_revision_code} — superseded
                                  </span>
                                );
                              }
                              if (!comp.revision_code) return null;
                              if (comp.is_current_revision === false) {
                                return (
                                  <span className="badge bg-warning text-dark ms-2" title="A newer revision of this part exists">
                                    Rev {comp.revision_code} — superseded
                                  </span>
                                );
                              }
                              return (
                                <span className="badge ms-2" title="This assembly uses the current revision of this part"
                                      style={{ backgroundColor: "#d1e7dd", color: "#0f5132", fontWeight: 600 }}>
                                  Rev {comp.revision_code} ✓
                                </span>
                              );
                            })()}
                          </div>
                          <small className="text-muted">{comp.description}</small>
                        </td>
                        <td className="text-end text-nowrap">
                          {comp.is_primary ? (
                            <>
                              {qty}<span className="text-muted small"> ppq</span>
                              {ppqUnsaved && (
                                <span className="badge bg-warning text-dark ms-1" title="Primary Part Qty changed above — Save Changes to persist it">unsaved</span>
                              )}
                            </>
                          ) : (isEditing
                            ? <input type="number" className="form-control form-control-sm text-end" value={editingCompQty} onChange={(e) => setEditingCompQty(e.target.value)} style={{ width: "70px" }} min="1" />
                            : qty)}
                        </td>
                        <td className="text-end">
                          {fmtMoney(v.material, { dash: true })}
                          {v.materialMethod && <div className="text-muted" style={{ fontSize: "0.7rem" }}>{v.materialMethod}</div>}
                        </td>
                        <td className="text-end">{fmtMoney(v.labor, { dash: true })}</td>
                        <td className="text-end" title={v.gas === null && v.consumables === null ? "not on this API row" : `gas ${fmtMoney(v.gas)} + consumables ${fmtMoney(v.consumables)}`}>
                          {(v.gas === null && v.consumables === null) ? <span className="text-muted">—</span> : fmtMoney((v.gas || 0) + (v.consumables || 0))}
                        </td>
                        <td className="text-end">
                          {fmtMoney(v.coating, { dash: true })}
                          {coatingOnOps && (
                            <div>
                              <span className="badge bg-info text-dark" title="The assembly prices Coating on its own operations row; the quote engine strips this part-level coating and the card subtracts it from shop cost (F07)">
                                moved to ops
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="text-end" title={v.costUpdatedAt ? asOf(v.costUpdatedAt) : "parts.cost_labor_material as saved"}>
                          {fmtMoney(v.stored, { dash: true })}
                          {v.costUpdatedAt && <div className="text-muted" style={{ fontSize: "0.7rem" }}>{asOf(v.costUpdatedAt)}</div>}
                        </td>
                        <td className="text-end text-nowrap" title={v.live === null
                          ? "This API row carries no gas/consumable components, so the rollup cannot be recomputed here (needs the provenance fields on GET /assemblies/{id}/components)"
                          : componentRollupFormula(v) || ""}>
                          {v.live === null
                            ? <span className="text-muted">n/a</span>
                            : (
                              <>
                                {fmtMoney(v.live)}
                                {v.stale
                                  ? <span className="badge bg-warning text-dark ms-1">stale {fmtDelta(v.delta)}</span>
                                  : <span className="badge bg-light text-dark border ms-1" title="stored total = live total">✓</span>}
                              </>
                            )}
                        </td>
                        <td className="text-end">
                          {v.bookPrice > 0
                            ? fmtMoney(v.bookPrice)
                            : <span className="text-muted" title="No book price on this part (parts.price is null/0) — written by quote approval or typed on the Part page">—</span>}
                          {v.bookPriceSource && (
                            <div className="text-muted" style={{ fontSize: "0.7rem" }}>{priceProvenanceLabel({ source: v.bookPriceSource, quoteId: v.bookPriceQuoteId, setAt: v.bookPriceSetAt })}</div>
                          )}
                        </td>
                        <td className="text-end" title={costSubtotal === null ? "" : `${qty} × ${fmtMoney(unitForSubtotal)} (${v.live !== null ? "live" : "stored — no live rollup"})`}>
                          {fmtMoney(costSubtotal, { dash: true })}
                        </td>
                        <td className="text-end" title={priceSubtotal === null ? "no book price" : `${qty} × ${fmtMoney(v.bookPrice)}`}>{fmtMoney(priceSubtotal, { dash: true })}</td>
                        <td>
                          {!comp.is_primary && (isEditing ? (
                            <>
                              <button className="btn btn-success btn-sm me-1" onClick={() => handleSaveEdit(comp)}>Save</button>
                              <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-sm btn-primary me-1" onClick={() => beginEdit(comp)}>Edit</button>
                              <button className="btn btn-sm btn-danger" onClick={() => handleRemoveComponent(comp)}>Remove</button>
                            </>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}

            <div className="card mt-4" style={{ maxWidth: "500px" }}>
              <div className="card-body">
                <h6>Add Part Component</h6>
                {/* NEW: Customer Filter for Add Component */}
                <div className="mb-2">
                    <label className="form-label text-primary">Filter Parts by Customer</label>
                    <select 
                        className="form-select form-select-sm border-primary" 
                        value={componentPartCustomerFilter} 
                        onChange={(e) => setComponentPartCustomerFilter(e.target.value)}
                    >
                        <option value="">-- All Customers --</option>
                        {customersList.map(c => (
                            <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                <div className="mb-2">
                  <label className="form-label">Enter or Select Product ID</label>
                  <input type="text" list="partsListDatalist" className="form-control" placeholder="Type product_id or pick from suggestions" value={newCompProductId} onChange={(e) => setNewCompProductId(e.target.value)} />
                  <datalist id="partsListDatalist">{filteredComponentParts.map((p) => (<option key={p.part_id} value={p.product_id}>{p.product_id} - {p.description}</option>))}</datalist>
                </div>
                <div className="mb-3">
                  <label className="form-label">Quantity Per Assembly</label>
                  <input type="number" className="form-control" min="1" value={newCompQty} onChange={(e) => setNewCompQty(e.target.value)} />
                </div>
                <button className="btn btn-success" onClick={handleAddComponent}>Add Part to Assembly</button>
              </div>
            </div>

            <hr className="my-4" />

            <h5>Purchased Components for Assembly #{assemblyId}</h5>
            <div className="text-muted mb-1" style={{ fontSize: "0.75rem" }}>
              Cost/unit = preferred vendor cost, else the highest vendor cost · Book price = consumables.price (written by quote approval) ·
              Suggested = cost × (1 + {fmtPct(ASSEMBLY_DEFAULT_QUOTE_RATES.material_markup, 0)}) — what the quotable price uses; a book price below cost is flagged (F23).
            </div>
            {assemblyConsumables.length === 0 ? <div>No purchased components yet.</div> : (
              <table className="table table-sm table-bordered align-middle">
                <thead className="table-light">
                    <tr>
                        <th>Component</th>
                        <th className="text-end">Qty</th><th className="text-center">Ships loose</th>
                        <th className="text-end">Cost/Unit</th>
                        <th className="text-end">Book Price</th>
                        <th className="text-end">Suggested price</th>
                        <th className="text-end">Cost Subtotal</th>
                        <th className="text-end">Price Subtotal <span className="fw-normal text-muted">(book)</span></th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {assemblyConsumables.map(cons => {
                        const isEditing = editingConsumableId === cons.assembly_consumable_id;
                        const v = consumableView(cons, ASSEMBLY_DEFAULT_QUOTE_RATES.material_markup);
                        const qty = cons.quantity_per_assembly || 0;
                        const costSubtotal = v.cost === null ? null : qty * v.cost;
                        const priceSubtotal = v.hasBook ? qty * v.book : null;
                        return (
                            <tr key={cons.assembly_consumable_id}>
                                <td><div><strong>{cons.product_id}</strong></div><small className="text-muted">{cons.description}</small></td>
                                <td className="text-end">{isEditing ? <input type="number" className="form-control form-control-sm text-end" style={{ width: "70px" }} value={editingConsumableQty} onChange={e => setEditingConsumableQty(e.target.value)} /> : qty}</td>
                                <td className="text-center" title="Ships loose with the assembly (bolt-on slats, hardware) instead of built in — the packing plan can put it in a kit box / bundle and it is listed on the manifests.">
                                  {isEditing
                                    ? <input type="checkbox" className="form-check-input" checked={!!editingConsumableLoose} onChange={e => setEditingConsumableLoose(e.target.checked)} />
                                    : (cons.ships_loose ? <span className="badge bg-warning text-dark">loose</span> : <span className="text-muted">built in</span>)}
                                </td>
                                <td className="text-end">
                                  {fmtMoney(v.cost, { dash: true })}
                                  <div className="text-muted" style={{ fontSize: "0.7rem" }}>{v.costVendorLabel || v.costSource}</div>
                                </td>
                                <td className="text-end text-nowrap">
                                  {v.hasBook
                                    ? fmtMoney(v.book)
                                    : <span className="text-muted" title="No book price on this consumable (consumables.price is null/0)">—</span>}
                                  {v.belowCost && (
                                    <span className="badge bg-danger ms-1" title={`Book ${fmtMoney(v.book)} is below the vendor cost ${fmtMoney(v.cost)} (${v.costVendorLabel || v.costSource}) — the quote must not seed this price`}>
                                      below cost{v.costVendorLabel ? ` — ${v.costVendorLabel}` : ''}
                                    </span>
                                  )}
                                  {v.bookSource && (
                                    <div className="text-muted" style={{ fontSize: "0.7rem" }}>{priceProvenanceLabel({ source: v.bookSource, quoteId: v.bookQuoteId, setAt: v.bookSetAt })}</div>
                                  )}
                                </td>
                                <td className="text-end" title={v.suggestedFormula || ""}>{fmtMoney(v.suggested, { dash: true })}</td>
                                <td className="text-end" title={costSubtotal === null ? "" : `${qty} × ${fmtMoney(v.cost)}`}>{fmtMoney(costSubtotal, { dash: true })}</td>
                                <td className="text-end" title={priceSubtotal === null ? "no book price" : `${qty} × ${fmtMoney(v.book)}`}>{fmtMoney(priceSubtotal, { dash: true })}</td>
                                <td>
                                    {isEditing ? (
                                        <><button className="btn btn-success btn-sm me-1" onClick={() => handleSaveEditConsumable(cons)}>Save</button><button className="btn btn-secondary btn-sm" onClick={cancelEditConsumable}>Cancel</button></>
                                    ) : (
                                        <><button className="btn btn-primary btn-sm me-1" onClick={() => beginEditConsumable(cons)}>Edit</button><button className="btn btn-danger btn-sm" onClick={() => handleRemoveConsumableComponent(cons)}>Remove</button></>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
              </table>
            )}
            
            <div className="card mt-4" style={{ maxWidth: "500px" }}>
              <div className="card-body">
                <h6>Add Purchased Component</h6>
                <div className="mb-2">
                  <label className="form-label">Select Component</label>
                  <select className="form-select" value={newConsumableId} onChange={e => setNewConsumableId(e.target.value)}>
                    <option value="">-- Choose a component --</option>
                    {consumablesList.map(c => (<option key={c.consumable_id} value={c.consumable_id}>{c.product_id} - {c.description}</option>))}
                  </select>
                </div>
                <div className="mb-3"><label className="form-label">Quantity</label><input type="number" className="form-control" min="1" value={newConsumableQty} onChange={e => setNewConsumableQty(e.target.value)} /></div>
                <button className="btn btn-success" onClick={handleAddConsumableComponent}>Add Component to Assembly</button>
              </div>
            </div>

            <hr className="my-4" />

            <div className="card mb-4" style={{ maxWidth: "500px" }}>
              <h5 className="card-header">Assembly Image</h5>
              <div className="card-body">
                {selectedAssembly?.image_path ? (
                  <img src={selectedAssembly.image_path} alt="Assembly" style={{ width: "100%", maxHeight: "400px", objectFit: "contain", cursor: "pointer" }} className="img-thumbnail mb-2 dark-invertible" onClick={() => setEnlargedImage(selectedAssembly.image_path)} />
                ) : (
                  <div className="text-center p-4" style={{ border: '2px dashed #ccc' }}>No image</div>
                )}
                <div className="input-group mt-2">
                  <input type="file" className="form-control" accept="image/*" onChange={e => setAssemblyImageFile(e.target.files[0])} />
                  <button className="btn btn-sm btn-secondary" type="button" onClick={handleUploadAssemblyImage} disabled={!assemblyId || !assemblyImageFile}>Upload</button>
                </div>
                {assemblyImageFile && (
                  <div style={{ marginTop: "1rem" }}>
                    <p className="small mb-1">Preview:</p>
                    <img src={URL.createObjectURL(assemblyImageFile)} alt="Pending upload preview" style={{ maxWidth: "100%", border: "1px solid #ccc" }} />
                  </div>
                )}
              </div>
            </div>

            <h5>Assembly Documents</h5>
            {assemblyDocuments.length > 0 && (
              <table className="table table-sm">
                <tbody>
                  {assemblyDocuments.map(doc => (
                    <tr key={doc.assembly_document_id}>
                      <td><a href={doc.file_url} target="_blank" rel="noopener noreferrer">{doc.original_filename}</a></td>
                      <td><small className="text-muted">Uploaded by {doc.uploaded_by_name} on {new Date(doc.created_at).toLocaleDateString()}</small></td>
                      <td><button className="btn btn-danger btn-sm" onClick={() => handleDeleteDocument(doc)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="card mt-2" style={{ maxWidth: "500px" }}>
              <div className="card-body">
                <h6>Upload New Document</h6>
                <div className="mb-3"><input type="file" className="form-control" onChange={e => setFileToUpload(e.target.files[0])} /></div>
                <button className="btn btn-info" onClick={handleFileUpload} disabled={isUploading}>{isUploading ? 'Uploading...' : 'Upload File'}</button>
              </div>
            </div>

            <hr className="my-4" />

            {/* Cost & price card (F02/F04/F07/F20/F22). Replaces the old two-row
                "Total Cost (Parts + Components + Operations)" vs "Total Price
                (Parts + Components)" table, which compared an ops-INCLUSIVE cost
                (with the primary's coating counted twice) against an
                ops-EXCLUSIVE book price and named neither basis. */}
            <AssembliesManagerPricingCard
              model={cardModel}
              totals={cardTotals}
              drift={cardDrift}
              pricingStatus={pricingInfo}
              reloading={pricingInfo.status === 'loading'}
              onReload={() => { loadPricing(assemblyId); loadOpsCost(assemblyId); }}
              ppq={Math.max(1, parseInt(primaryPartQuantity, 10) || 1)}
              ppqUnsaved={ppqUnsaved}
              assemblyWeight={assemblyWeight}
            />

            <div className="row justify-content-end">
              <div className="col-md-5">
                  <label htmlFor="assemblyPrice" className="form-label mt-2">
                    <strong>System Price</strong> <span className="text-muted">(assemblies.assembly_price — per set)</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input type="number" step="0.01" min="0" id="assemblyPrice" className="form-control" placeholder="not set"
                           value={assemblyPrice} onChange={e => setAssemblyPrice(e.target.value)} />
                  </div>
                  <div className="form-text">
                    Written by quote approval (the quote's per-set price for this assembly) or typed here (stored as "manual").
                    Saved with <strong>Save Changes</strong> only when it changed; clearing a stored value asks first.
                    {' '}Stored now: {loadedAssemblyPrice > 0
                      ? <>{fmtMoney(loadedAssemblyPrice)} — {priceProvenanceLabel({
                          source: cardModel.system_price.source, quoteId: cardModel.system_price.quote_id, setAt: cardModel.system_price.set_at })}</>
                      : 'not set'}.
                    {cardTotals.cost_live !== null && assemblyPrice !== '' && parseFloat(assemblyPrice) > 0 && (
                      <> Margin vs shop cost at {fmtMoney(parseFloat(assemblyPrice))}: <strong>{fmtPct((parseFloat(assemblyPrice) - cardTotals.cost_live) / parseFloat(assemblyPrice))}</strong>
                        {parseFloat(assemblyPrice) < cardTotals.cost_live && <span className="badge bg-danger ms-1">below cost</span>}</>
                    )}
                  </div>
              </div>
            </div>
          </>
        )}

        {enlargedImage && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeEnlarge}>
            <button onClick={closeEnlarge} style={{ position: "absolute", top: "20px", right: "20px", background: "white", border: "1px solid #ccc", fontSize: "1.5rem", borderRadius: "50%", width: "40px", height: "40px", cursor: "pointer", textAlign: "center", lineHeight: "36px" }}>&times;</button>
            <img src={enlargedImage} alt="Enlarged" style={{ maxWidth: "80vw", maxHeight: "80vh" }} onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </div>
    </div>
  );
}